import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, Loader2, ArrowLeft, MapPin, Trophy, Edit, Trash2, Map as MapIcon, Image as ImageIcon, X, ImagePlus, Eye, EyeOff, Plus, Shield, User, Send, Home, Target, Play, Waypoints, Save, Menu, Search, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// import { toast } from "sonner";
const toast = {
  error: () => {},
  success: () => {},
  info: () => {},
  loading: () => {}
};
import { useAuth } from "@/contexts/AuthContext";
import { useVideo } from "@/contexts/VideoContext";
import { MapContainer, TileLayer, Marker, Polyline, Polygon as LeafletPolygon, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import AllAnswersMap from "./AllAnswersMap";
import WinnersPodium from "./WinnersPodium";
import Accordion from "./Accordion";
import "./Accordion.css";
import Avatar from "./Avatar";
import ResultsTable from "./ResultsTable";
import VideoTimecodeEditor from "./VideoTimecodeEditor";
import InteractiveGamePlayer from "./InteractiveGamePlayer";
import VideoStatsWidget from './VideoStatsWidget';
import GameVideoAnalytics from './GameVideoAnalytics';
import PlaceMedal from './PlaceMedal';
import GameWinnersStrip from './GameWinnersStrip';
import SiteFooter from './SiteFooter';
import AnnouncementStats from './AnnouncementStats';
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Prize Video HLS Player Component
const PrizeVideoPlayer = ({ gameId, place, poster, style }) => {
  const videoRef = useRef(null);
  const [hlsReady, setHlsReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastCheck, setLastCheck] = useState(0);
  
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/games/${gameId}/prize/${place}/video/status?t=${Date.now()}`);
        const wasProcessing = processing;
        setProcessing(response.data.processing);
        setHlsReady(response.data.hls_ready);
        setLastCheck(Date.now());
        
        // Force re-render when status changes
        if (wasProcessing && !response.data.processing) {
          console.log(`Prize ${place} video processing completed!`);
        }
      } catch (error) {
        console.error('Error checking prize video status:', error);
      }
    };
    
    checkStatus();
    // Poll every 3 seconds if processing, otherwise every 10 seconds
    const pollInterval = processing ? 3000 : 10000;
    const interval = setInterval(checkStatus, pollInterval);
    return () => clearInterval(interval);
  }, [gameId, place, processing]);
  
  useEffect(() => {
    if (!hlsReady || !videoRef.current) return;
    
    const video = videoRef.current;
    const hlsUrl = `${API}/games/${gameId}/prize/${place}/video/hls/master.m3u8`;
    
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
    } else if (window.Hls && window.Hls.isSupported()) {
      const hls = new window.Hls({ maxBufferLength: 30, maxMaxBufferLength: 60 });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      
      hls.on(window.Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case window.Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case window.Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });
      
      return () => hls.destroy();
    }
  }, [hlsReady, gameId, place]);
  
  if (processing) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', color: '#fff' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 className="animate-spin" size={32} />
          <p style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>{t('gamePageUI.processing')}</p>
        </div>
      </div>
    );
  }
  
  if (!hlsReady) return null;
  
  return <video ref={videoRef} controls style={style} poster={poster}>{t('gamePageUI.browserNotSupportVideo')}</video>;
};

// HLS Video Player Component
const HLSPlayer = ({ gameId, poster, style }) => {
  const videoRef = useRef(null);
  const [hlsReady, setHlsReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastCheck, setLastCheck] = useState(0);
  const [processingInfo, setProcessingInfo] = useState(null);
  
  useEffect(() => {
    // Check video processing status with cache busting
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/games/${gameId}/video/status?t=${Date.now()}`);
        const wasProcessing = processing;
        const st = response.data;
        setProcessing(st.processing);
        setHlsReady(st.hls_ready);
        setLastCheck(Date.now());
        
        if (st.processing) {
          setProcessingInfo({
            step: st.processing_step || 'Обработка...',
            progress: st.processing_progress || 0,
            etaSec: st.processing_eta_sec || 0,
            elapsedSec: st.processing_elapsed_sec || 0,
            fileSizeMb: st.video_file_size_mb || 0,
            durationSec: st.video_duration_sec || 0
          });
        } else {
          setProcessingInfo(null);
        }
        
        if (wasProcessing && !st.processing) {
          console.log('Video processing completed!');
        }
      } catch (error) {
        console.error('Error checking video status:', error);
      }
    };
    
    checkStatus();
    
    const pollInterval = processing ? 3000 : 10000;
    const interval = setInterval(checkStatus, pollInterval);
    return () => clearInterval(interval);
  }, [gameId, processing]);
  
  useEffect(() => {
    if (!hlsReady || !videoRef.current) return;
    
    const video = videoRef.current;
    const hlsUrl = `${API}/games/${gameId}/video/hls/master.m3u8`;
    
    // Check if HLS is natively supported
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
    } else if (window.Hls && window.Hls.isSupported()) {
      // Use hls.js for browsers that don't support HLS natively
      const hls = new window.Hls({
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
      });
      
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      
      hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest loaded, starting playback');
      });
      
      hls.on(window.Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case window.Hls.ErrorTypes.NETWORK_ERROR:
              console.log('Network error, trying to recover');
              hls.startLoad();
              break;
            case window.Hls.ErrorTypes.MEDIA_ERROR:
              console.log('Media error, trying to recover');
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });
      
      return () => {
        hls.destroy();
      };
    }
  }, [hlsReady, gameId]);
  
  if (processing) {
    const info = processingInfo || {};
    return (
      <div style={{ 
        ...style, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#0f172a',
        color: '#fff',
        borderRadius: '12px',
        padding: '2rem'
      }}>
        <div style={{ 
          width: '56px', height: '56px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '1rem', position: 'relative'
        }}>
          <Loader2 className="animate-spin" size={28} style={{ color: '#fff' }} />
          {info.progress > 0 && info.progress < 100 && (
            <svg style={{ position: 'absolute', top: -3, left: -3, width: 62, height: 62, transform: 'rotate(-90deg)' }}>
              <circle cx="31" cy="31" r="28" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
              <circle cx="31" cy="31" r="28" fill="none" stroke="#a5b4fc" strokeWidth="3"
                strokeDasharray={`${2 * Math.PI * 28}`}
                strokeDashoffset={`${2 * Math.PI * 28 * (1 - info.progress / 100)}`}
                strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </svg>
          )}
        </div>
        <p style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem', color: '#e0e7ff' }}>
          {t('gamePageUI.videoProcessing')}
        </p>
        <p style={{ fontSize: '0.95rem', marginBottom: '1rem', color: '#a5b4fc' }}>
          {info.step || 'Подготовка...'}
        </p>
        <div style={{
          width: '85%', maxWidth: '450px', height: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '5px', overflow: 'hidden', marginBottom: '0.75rem'
        }}>
          <div style={{
            width: `${info.progress || 0}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #6366f1, #a78bfa)',
            borderRadius: '5px',
            transition: 'width 1s ease'
          }} />
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', color: '#94a3b8' }}>
          <span>{info.progress || 0}%</span>
          {info.elapsedSec > 0 && (
            <span>Прошло: {Math.floor(info.elapsedSec / 60)}:{String(info.elapsedSec % 60).padStart(2, '0')}</span>
          )}
          {info.etaSec > 0 && (
            <span>Осталось: ~{Math.floor(info.etaSec / 60)}:{String(info.etaSec % 60).padStart(2, '0')}</span>
          )}
        </div>
        {info.fileSizeMb > 0 && (
          <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.75rem' }}>
            {info.fileSizeMb.toFixed(0)} МБ, {Math.floor((info.durationSec || 0) / 60)}:{String(Math.floor((info.durationSec || 0) % 60)).padStart(2, '0')} мин
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          {['360p', '480p', '720p', '1080p'].map((q, i) => {
            const done = (info.progress || 0) > ((i + 1) / 4) * 100;
            const active = info.step && info.step.includes(q);
            return (
              <div key={q} style={{
                padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 500,
                background: done ? 'rgba(34,197,94,0.2)' : active ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
                color: done ? '#4ade80' : active ? '#a5b4fc' : '#64748b',
                border: `1px solid ${done ? 'rgba(34,197,94,0.3)' : active ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.1)'}`,
                transition: 'all 0.3s ease'
              }}>
                {done ? '\u2713 ' : active ? '\u25C9 ' : '\u25CB '}{q}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  
  if (!hlsReady) {
    return (
      <div style={{ 
        ...style, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#000',
        color: '#fff'
      }}>
        <p>{t('gamePageUI.videoUnavailable')}</p>
      </div>
    );
  }
  
  return (
    <video
      ref={videoRef}
      controls
      style={style}
      poster={poster}
    >
      Ваш браузер не поддерживает видео.
    </video>
  );
};

const GamePage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user, hasRole, getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  // Pick translated text from a translations dict for the current language with safe fallbacks
  const getTranslatedText = (translations, fallback) => {
    if (!translations) return fallback;
    if (translations[language]) return translations[language];
    if (translations['ru']) return translations['ru'];
    return fallback;
  };
  const { startVideo, startVideoEmbedded, startVideoAtTime, isActive: isVideoActive, seek, mode, videoRef, isPlaying, togglePlay, currentTime, duration, timecodes, showControls, setShowControls, stopVideo, setIsFullscreen, fmt } = useVideo();
  const [game, setGame] = useState(null);
  const [panoramas, setPanoramas] = useState([]);
  const [allHotspots, setAllHotspots] = useState([]);
  const [foundHotspotIds, setFoundHotspotIds] = useState([]);
  const [results, setResults] = useState([]);
  const [myResults, setMyResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [deleting, setDeleting] = useState(null);
  const [deletingGame, setDeletingGame] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [annRefreshKey, setAnnRefreshKey] = useState(0);
  const coverInputRef = useRef(null);
  const [showAllAnswersMap, setShowAllAnswersMap] = useState(false);
  const [winners, setWinners] = useState([]);
  const fileInputRef = useRef(null);
  const [uploadingPreview, setUploadingPreview] = useState(null);
  const previewFileInputRefs = useRef({});
  const videoInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const jpgInputRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [videoUploadSpeed, setVideoUploadSpeed] = useState(0);
  const [videoUploadEta, setVideoUploadEta] = useState(0);
  const [videoFileInfo, setVideoFileInfo] = useState(null);
  const [videoProcessingInfo, setVideoProcessingInfo] = useState(null);
  
  // JPG Panorama upload states
  const [uploadingJpgs, setUploadingJpgs] = useState([]);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [processingPanoramas, setProcessingPanoramas] = useState([]);
  
  // Prize upload states - now tracking progress for each place
  const [uploadingPrize, setUploadingPrize] = useState(null); // format: "1-image" or "2-video"
  const [prizeVideoProgress, setPrizeVideoProgress] = useState({
    1: 0,
    2: 0,
    3: 0
  });
  
  // Video cover states
  const [uploadingVideoCover, setUploadingVideoCover] = useState(false);
  
  // Game activation states
  const [gameActivated, setGameActivated] = useState(false);
  const [activatingGame, setActivatingGame] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  
  // Registration system states
  const [registrationInfo, setRegistrationInfo] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [countdown, setCountdown] = useState(null);
  // Video timecodes for description links
  const [videoTimecodes, setVideoTimecodes] = useState([]);
  
  // Window width for responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Drag and drop states for reordering panoramas
  const [draggedPanorama, setDraggedPanorama] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Hint polygon editor
  const [hintPolygonEditor, setHintPolygonEditor] = useState(null); // panoramaId when editing
  const [hintPolygonPoints, setHintPolygonPoints] = useState([]);
  const [hintPolygonClosed, setHintPolygonClosed] = useState(false);
  const [hintPolygonIsNew, setHintPolygonIsNew] = useState(false);
  const [hintPolygonSaving, setHintPolygonSaving] = useState(false);
  const [hintPolygonExisting, setHintPolygonExisting] = useState({});
  const [hintMenuOpen, setHintMenuOpen] = useState(false);
  const [hintPolygonCost, setHintPolygonCost] = useState(0);
  const [hintMapType, setHintMapType] = useState('map'); // {panoramaId: true/false}

  useEffect(() => {
    fetchGameData();
  }, [gameId]);

  // On hard reload, `user` is null when the initial fetchGameData runs,
  // so my-results is skipped. Re-fetch as soon as user becomes available.
  useEffect(() => {
    if (!user || !gameId) return;
    axios.get(`${API}/games/${gameId}/my-results`, { headers: getAuthHeader() })
      .then((r) => setMyResults(r.data))
      .catch(() => {});
  }, [user, gameId]);

  // Same race: foundHotspotIds is gated on user. Re-fetch when user becomes available.
  useEffect(() => {
    if (!user || !panoramas || panoramas.length === 0) return;
    const ids = panoramas.map((p) => p.id);
    Promise.all(
      ids.map((id) =>
        axios.get(`${API}/panoramas/${id}/hotspots/found`, { headers: getAuthHeader() })
          .catch(() => ({ data: { found_hotspot_ids: [] } }))
      )
    ).then((results) => {
      setFoundHotspotIds(results.flatMap((r) => r.data?.found_hotspot_ids || []));
    });
  }, [user, panoramas]);
  
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-close modal when all uploads are completed
  useEffect(() => {
    if (uploadingJpgs.length > 0) {
      const allCompleted = uploadingJpgs.every(item => 
        item.status === 'completed' || item.status === 'failed'
      );
      
      // Debug: check current statuses
      if (uploadingJpgs.some(i => i.status === 'completed' || i.status === 'processing')) {
        console.log('Modal status:', uploadingJpgs.map(i => `${i.id.slice(0,8)}: ${i.status} (${i.progress}%)`));
      }
      
      if (allCompleted) {
        console.log('✓ All completed! Closing in 2s...');
        const timer = setTimeout(() => {
          console.log('Closing modal and refreshing...');
          setShowProgressModal(false);
          setUploadingJpgs([]);
          fetchGameData();
        }, 2000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [uploadingJpgs]);

  // Fetch game activation status
  useEffect(() => {
    const fetchActivationStatus = async () => {
      if (!gameId) return;
      try {
        const headers = user ? getAuthHeader() : {};
        const resp = await axios.get(`${API}/games/${gameId}/activation-status`, { headers });
        setGameActivated(resp.data.activated);
        setUserPoints(resp.data.user_points || 0);
      } catch (error) {
        console.error('Error fetching activation status:', error);
      }
    };
    fetchActivationStatus();
  }, [gameId, user]);

  // Fetch registration info for collecting games
  useEffect(() => {
    const fetchRegistrationInfo = async () => {
      if (!gameId) return;
      try {
        const headers = user ? getAuthHeader() : {};
        const resp = await axios.get(`${API}/games/${gameId}/registration-info`, { headers });
        setRegistrationInfo(resp.data);
      } catch (error) {
        console.error('Error fetching registration info:', error);
      }
    };
    fetchRegistrationInfo();
  }, [gameId, user]);

  // Countdown timer for registration deadline
  useEffect(() => {
    if (!game?.registration_deadline || game?.game_status !== 'collecting') {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const deadline = new Date(game.registration_deadline);
      const now = new Date();
      const diff = deadline - now;

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ days, hours, minutes, seconds, expired: false });
      } else {
        setCountdown({ expired: true });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [game?.registration_deadline, game?.game_status]);

  // Handle game registration
  const handleRegisterForGame = async () => {
    if (!user || registering) return;
    
    const pointsRequired = game?.points_required || 0;
    if (!window.confirm(`Подтвердите участие в игре.\n\nБудет списано: ${pointsRequired} баллов\nВаш баланс: ${Math.round(registrationInfo?.user_points || 0)} баллов`)) {
      return;
    }
    
    setRegistering(true);
    try {
      const resp = await axios.post(`${API}/games/${gameId}/register`, {}, {
        headers: getAuthHeader()
      });
      toast.success(resp.data.message);
      // Refresh registration info
      const regResp = await axios.get(`${API}/games/${gameId}/registration-info`, { 
        headers: getAuthHeader() 
      });
      setRegistrationInfo(regResp.data);
    } catch (error) {
      console.error('Error registering for game:', error);
      toast.error(error.response?.data?.detail || 'Ошибка регистрации');
    } finally {
      setRegistering(false);
    }
  };

  // Handle game unregistration
  const handleUnregisterFromGame = async () => {
    if (!user || registering) return;
    
    if (!window.confirm('Вы уверены, что хотите отменить регистрацию? Баллы будут возвращены.')) {
      return;
    }
    
    setRegistering(true);
    try {
      const resp = await axios.post(`${API}/games/${gameId}/unregister`, {}, {
        headers: getAuthHeader()
      });
      toast.success(resp.data.message);
      // Refresh registration info
      const regResp = await axios.get(`${API}/games/${gameId}/registration-info`, { 
        headers: getAuthHeader() 
      });
      setRegistrationInfo(regResp.data);
    } catch (error) {
      console.error('Error unregistering from game:', error);
      toast.error(error.response?.data?.detail || 'Ошибка отмены регистрации');
    } finally {
      setRegistering(false);
    }
  };

  // Handle game activation
  const handleActivateGame = async () => {
    if (!user || activatingGame) return;
    
    setActivatingGame(true);
    try {
      const resp = await axios.post(`${API}/games/${gameId}/activate`, {}, {
        headers: getAuthHeader()
      });
      setGameActivated(true);
      setUserPoints(resp.data.new_balance);
      toast.success(resp.data.message || 'Игра активирована!');
    } catch (error) {
      console.error('Error activating game:', error);
      toast.error(error.response?.data?.detail || 'Ошибка активации');
    } finally {
      setActivatingGame(false);
    }
  };

  const fetchGameData = async () => {
    try {
      setLoading(true);

      // Fire-and-forget view tracking
      axios.post(`${API}/games/${gameId}/view`, {}, { headers: getAuthHeader() }).catch(() => {});

      // Kick off all independent fetches in parallel; each updates state as it lands
      const gameP = axios.get(`${API}/games/${gameId}`, { headers: getAuthHeader() })
        .then((r) => { setGame(r.data); return r; });

      const panoramasP = axios.get(`${API}/games/${gameId}/panoramas`)
        .then((r) => { setPanoramas(r.data); return r; });

      // Results (public)
      axios.get(`${API}/games/${gameId}/results`)
        .then((r) => setResults((r.data || []).sort((a, b) => b.total_score - a.total_score)))
        .catch(() => {});

      // My results — surface ASAP, in parallel with everything else
      if (user) {
        axios.get(`${API}/games/${gameId}/my-results`, { headers: getAuthHeader() })
          .then((r) => { console.log('My results:', r.data); setMyResults(r.data); })
          .catch(() => {});
      }

      // Winners (public)
      axios.get(`${API}/games/${gameId}/winners`)
        .then((r) => setWinners(r.data))
        .catch(() => setWinners([]));

      // Video timecodes
      axios.get(`${API}/games/${gameId}/video/timecodes`)
        .then((r) => setVideoTimecodes(r.data || []))
        .catch(() => setVideoTimecodes([]));

      // Hotspots / found / hint polygons depend on panorama IDs — start as soon as panoramas arrive
      panoramasP.then((panoramasResponse) => {
        const ids = (panoramasResponse.data || []).map((p) => p.id);

        // All hotspots in parallel
        Promise.all(
          ids.map((id) => axios.get(`${API}/panoramas/${id}/hotspots`).catch(() => ({ data: [] })))
        ).then((results) => {
          setAllHotspots(results.flatMap((r) => r.data || []));
        });

        // Found hotspots for current user in parallel
        if (user) {
          Promise.all(
            ids.map((id) =>
              axios.get(`${API}/panoramas/${id}/hotspots/found`, { headers: getAuthHeader() })
                .catch(() => ({ data: { found_hotspot_ids: [] } }))
            )
          ).then((results) => {
            setFoundHotspotIds(results.flatMap((r) => r.data?.found_hotspot_ids || []));
          });
        }

        // Admin/editor: hint polygons
        if (hasRole(['admin', 'editor'])) {
          const hintMap = {};
          Promise.all(
            ids.map((id) =>
              axios.get(`${API}/hints/polygon/${id}`)
                .then(() => { hintMap[id] = true; })
                .catch(() => { hintMap[id] = false; })
            )
          ).then(() => setHintPolygonExisting({ ...hintMap }));
        }
      }).catch(() => {});

      // Hide the page-level loader once the two critical pieces are in
      await Promise.all([gameP, panoramasP]);
    } catch (error) {
      console.error("Error fetching game data:", error);
      toast.error(t('gamePageUI.gameLoadingError'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Reset found hotspots (admin/editor only)
  const handleResetHotspots = async () => {
    if (!window.confirm('Сбросить все найденные полигоны в этой игре?\nВаши баллы за них будут вычтены.')) return;
    
    try {
      const response = await axios.delete(`${API}/games/${gameId}/hotspots/reset`, {
        headers: getAuthHeader()
      });
      
      toast.success(`${response.data.message}. Вычтено ${response.data.points_deducted} баллов`);
      setFoundHotspotIds([]);
      
      // Refresh user points
      try {
        const userResp = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
        setUserPoints(userResp.data.total_points || 0);
      } catch (e) {}
    } catch (error) {
      console.error('Error resetting hotspots:', error);
      toast.error(t('gamePageUI.errorResettingResults'));
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    await uploadPanoramas(files);
  };

  const handleZipSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      toast.error(t('gamePageUI.pleaseSelectZipFile'));
      return;
    }
    
    await uploadZip(file);
  };

  const uploadZip = async (zipFile) => {
    console.log('=== Starting ZIP upload ===');
    console.log('ZIP file:', zipFile.name, 'Size:', zipFile.size);
    
    setUploading(true);
    setUploadProgress({ current: 0, total: 0 });
    
    try {
      const formData = new FormData();
      formData.append('file', zipFile);
      
      const response = await axios.post(
        `${API}/panoramas/upload-zip?game_id=${gameId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress({ current: progressEvent.loaded, total: progressEvent.total });
            console.log(`Upload progress: ${progress}%`);
          }
        }
      );
      
      console.log('ZIP upload response:', response.data);
      
      if (response.data.success) {
        toast.success(`✅ Загружено ${response.data.uploaded} панорам из ${response.data.total_found}`);
        console.log('Refreshing game data...');
        await fetchGameData();
      } else {
        toast.error(t('gamePageUI.zipArchiveDownloadError'));
      }
      
    } catch (error) {
      console.error('❌ Error uploading ZIP:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Ошибка загрузки ZIP');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      console.log('=== ZIP upload complete ===');
    }
  };

  // Handle JPG file selection
  const handleJpgSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setShowProgressModal(true);
    
    const uploadPromises = files.map((file, index) => uploadSingleJpg(file, index, files.length));
    await Promise.all(uploadPromises);
    
    // Reset input
    e.target.value = '';
  };

  // Upload single JPG file
  const uploadSingleJpg = async (file, index, total) => {
    const uploadId = `${Date.now()}_${index}`;
    
    // Add to uploading list
    setUploadingJpgs(prev => [...prev, {
      id: uploadId,
      name: file.name,
      status: 'uploading',
      progress: 0
    }]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/panoramas/upload-jpg?game_id=${gameId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadingJpgs(prev => prev.map(item => 
              item.id === uploadId ? { ...item, progress } : item
            ));
          }
        }
      );

      // Upload complete, start processing
      const panoramaId = response.data.id;
      
      setUploadingJpgs(prev => prev.map(item => 
        item.id === uploadId ? { ...item, status: 'processing', panoramaId } : item
      ));

      // Start polling for status
      pollPanoramaStatus(panoramaId, uploadId);

    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      setUploadingJpgs(prev => prev.map(item => 
        item.id === uploadId ? { ...item, status: 'error', error: error.response?.data?.detail || 'Upload failed' } : item
      ));
    }
  };

  // Poll panorama processing status
  const pollPanoramaStatus = (panoramaId, uploadId) => {
    let attempts = 0;
    const maxAttempts = 200; // 10 minutes (200 * 3 seconds)
    
    const poll = setInterval(async () => {
      attempts++;
      
      try {
        const response = await axios.get(`${API}/panoramas/${panoramaId}/privacy-status`, {
          headers: getAuthHeader()
        });
        
        const { status, progress, message } = response.data;
        
        // Debug: log when status changes to completed
        if (status === 'completed') {
          console.log(`✓ Panorama ${panoramaId.slice(0,8)} completed at ${progress}%`);
        }
        
        // Update status
        setUploadingJpgs(prev => prev.map(item => 
          item.id === uploadId ? { 
            ...item, 
            status: status === 'completed' || status === 'failed' ? status : 'processing',
            progress: progress || 0,
            message: message || ''
          } : item
        ));

        // Stop polling if completed, failed, or max attempts reached
        if (status === 'completed' || status === 'failed' || attempts >= maxAttempts) {
          clearInterval(poll);
          console.log(`Polling stopped for ${panoramaId.slice(0,8)}`);
        }
      } catch (error) {
        console.error('[Poll] Error polling status:', error);
        clearInterval(poll);
        setUploadingJpgs(prev => prev.map(item => 
          item.id === uploadId ? { ...item, status: 'failed', error: 'Status check failed' } : item
        ));
      }
    }, 3000); // Poll every 3 seconds
  };

  const uploadPanoramas = async (files) => {
    console.log('=== Starting uploadPanoramas ===');
    console.log('Total files:', files.length);
    
    // Group files by panorama
    const panoramaGroups = new Map();
    
    files.forEach(file => {
      const path = file.webkitRelativePath || file.name;
      console.log(`Processing file: ${path}`);
      const parts = path.split('/');
      
      if (parts.length > 1) {
        // Has directory structure
        const rootDir = parts[0];
        const subPath = parts.slice(1).join('/');
        
        console.log(`  rootDir: ${rootDir}, subPath: ${subPath}`);
        
        // Check if this is a panorama structure (N.html + Ndata/)
        // Pattern: rootDir/1.html, rootDir/1data/..., rootDir/2.html, rootDir/2data/...
        const htmlMatch = subPath.match(/^(\d+)\.html$/);
        const dataDirMatch = subPath.match(/^(\d+)data\//);
        
        if (htmlMatch) {
          // This is an HTML file (e.g., 1.html)
          const panoramaNum = htmlMatch[1];
          const key = `${rootDir}/${panoramaNum}`;
          console.log(`  -> HTML file for panorama: ${key}`);
          if (!panoramaGroups.has(key)) {
            panoramaGroups.set(key, []);
          }
          panoramaGroups.get(key).push(file);
        } else if (dataDirMatch) {
          // This is a file in data directory (e.g., 1data/...)
          const panoramaNum = dataDirMatch[1];
          const key = `${rootDir}/${panoramaNum}`;
          console.log(`  -> Data file for panorama: ${key}`);
          if (!panoramaGroups.has(key)) {
            panoramaGroups.set(key, []);
          }
          panoramaGroups.get(key).push(file);
        } else {
          // Other files in root directory, treat as single panorama
          const key = rootDir;
          console.log(`  -> Other file, grouping under: ${key}`);
          if (!panoramaGroups.has(key)) {
            panoramaGroups.set(key, []);
          }
          panoramaGroups.get(key).push(file);
        }
      } else {
        // Single file without directory
        console.log(`  -> Single file without directory structure`);
        if (!panoramaGroups.has('_single')) {
          panoramaGroups.set('_single', []);
        }
        panoramaGroups.get('_single').push(file);
      }
    });

    const panoramas = Array.from(panoramaGroups.entries());
    console.log(`=== Found ${panoramas.length} panorama(s) to upload ===`);
    panoramas.forEach(([key, files]) => {
      console.log(`  ${key}: ${files.length} files`);
    });
    
    setUploadProgress({ current: 0, total: panoramas.length });
    setUploading(true);

    try {
      for (let i = 0; i < panoramas.length; i++) {
        const [panoramaKey, panoramaFiles] = panoramas[i];
        setUploadProgress({ current: i + 1, total: panoramas.length });

        console.log(`=== Uploading panorama ${i + 1}/${panoramas.length}: ${panoramaKey} ===`);
        console.log(`Files (${panoramaFiles.length}):`, panoramaFiles.map(f => f.webkitRelativePath || f.name));

        const formData = new FormData();
        panoramaFiles.forEach((file) => {
          const rel = file.webkitRelativePath || file.name;
          formData.append("files", file, rel);
        });

        try {
          const response = await axios.post(`${API}/panoramas/upload?game_id=${gameId}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              ...getAuthHeader()
            },
          });
          
          console.log(`✅ Successfully uploaded: ${panoramaKey}`, response.data);
          
          if (panoramas.length === 1) {
            toast.success(t('gamePageUI.panoramaUploadedSuccessfully'));
          }
        } catch (error) {
          console.error(`❌ Error uploading ${panoramaKey}:`, error);
          console.error('Error details:', error.response?.data);
          toast.error(`Ошибка загрузки ${panoramaKey}: ${error.response?.data?.detail || error.message}`);
        }
      }

      if (panoramas.length > 1) {
        toast.success(`Загружено панорам: ${panoramas.length}`);
      }
      
      console.log('=== Refreshing game data ===');
      await fetchGameData();
    } catch (error) {
      console.error('=== Fatal error in uploadPanoramas ===', error);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const traverseDirectory = async (entry, files, parentPath) => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file) => {
          // Create a new file with webkitRelativePath
          const relativePath = parentPath + entry.name;
          const newFile = new File([file], file.name, { type: file.type });
          Object.defineProperty(newFile, 'webkitRelativePath', {
            value: relativePath,
            writable: false
          });
          files.push(newFile);
          console.log(`  Added file: ${relativePath}`);
          resolve();
        }, (error) => {
          console.error(`Error reading file ${entry.name}:`, error);
          resolve(); // Continue even if one file fails
        });
      } else if (entry.isDirectory) {
        const dirReader = entry.createReader();
        const newPath = parentPath + entry.name + '/';
        
        const readAllEntries = async () => {
          const allEntries = [];
          
          const readBatch = () => {
            return new Promise((resolveBatch) => {
              dirReader.readEntries((entries) => {
                if (entries.length > 0) {
                  allEntries.push(...entries);
                  readBatch().then(resolveBatch);
                } else {
                  resolveBatch();
                }
              }, (error) => {
                console.error(`Error reading directory ${entry.name}:`, error);
                resolveBatch();
              });
            });
          };
          
          await readBatch();
          return allEntries;
        };
        
        readAllEntries().then(async (entries) => {
          console.log(`  Directory ${entry.name} contains ${entries.length} entries`);
          for (const childEntry of entries) {
            await traverseDirectory(childEntry, files, newPath);
          }
          resolve();
        });
      } else {
        resolve();
      }
    });
  };

  const getPanoramaResult = (panoramaId) => {
    if (!myResults || !myResults.answers) return null;
    return myResults.answers.find(a => a.panorama_id === panoramaId);
  };

  const handlePublishToggle = async () => {
    try {
      const newPublishedState = !game.published;
      await axios.put(
        `${API}/games/${gameId}/publish?published=${newPublishedState}`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success(newPublishedState ? 'Игра опубликована' : 'Игра снята с публикации');
      fetchGameData();
    } catch (error) {
      console.error('Error publishing game:', error);
      toast.error(error.response?.data?.detail || 'Ошибка публикации');
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('gamePageUI.invalidFileFormatAllowed'));
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('gamePageUI.fileTooBigMaximum'));
      return;
    }
    
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('cover', file);
      
      const authHeaders = getAuthHeader();
      
      await axios.post(
        `${API}/games/${gameId}/cover`,
        formData,
        {
          headers: {
            ...authHeaders
            // Note: Do NOT set Content-Type for FormData - axios sets it automatically with boundary
          },
          timeout: 60000 // 60 second timeout for large files
        }
      );
      
      toast.success(t('gamePageUI.coverUploaded'));
      fetchGameData(); // Refresh to show new cover
    } catch (error) {
      console.error('Error uploading cover:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Ошибка загрузки обложки';
      toast.error(errorMessage);
    } finally {
      setUploadingCover(false);
    }
  };

  const handleDeleteCover = async () => {
    if (!window.confirm('Удалить обложку игры?')) {
      return;
    }
    
    setUploadingCover(true);
    try {
      await axios.delete(`${API}/games/${gameId}/cover`, {
        headers: getAuthHeader()
      });
      
      toast.success(t('gamePageUI.coverBeenRemoved'));
      fetchGameData();
    } catch (error) {
      console.error('Error deleting cover:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления обложки');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleDeletePanorama = async (panoramaId, e) => {
    e.stopPropagation();

    setDeleting(panoramaId);
    try {
      await axios.delete(`${API}/panoramas/${panoramaId}`, {
        headers: getAuthHeader()
      });
      toast.success(t('gamePageUI.panoramaDeleted'));
      fetchGameData();
    } catch (error) {
      console.error('Error deleting panorama:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления панорамы');
    } finally {
      setDeleting(null);
    }
  };

  const handleUploadCustomPreview = async (panoramaId, file) => {
    if (!file) return;
    
    setUploadingPreview(panoramaId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/panoramas/${panoramaId}/custom-preview`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            
          }
        }
      );
      
      fetchGameData(); // Refresh to show new preview
    } catch (error) {
      console.error('Error uploading custom preview:', error);
      toast.error(error.response?.data?.detail || 'Ошибка загрузки превью');
    } finally {
      setUploadingPreview(null);
    }
  };

  const handleDeleteCustomPreview = async (panoramaId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Удалить кастомное превью? Будет использовано автоматическое превью.')) {
      return;
    }
    
    setUploadingPreview(panoramaId);
    try {
      await axios.delete(`${API}/panoramas/${panoramaId}/custom-preview`, {
        headers: getAuthHeader()
      });
      
      fetchGameData(); // Refresh to show default preview
    } catch (error) {
      console.error('Error deleting custom preview:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления превью');
    } finally {
      setUploadingPreview(null);
    }
  };



  // ============= HINT POLYGON EDITOR =============
  const openHintPolygonEditor = async (panorama) => {
    setHintPolygonEditor(panorama.id);
    setHintPolygonPoints([]);
    setHintPolygonClosed(false);
    setHintPolygonIsNew(false);
    setHintPolygonCost(0);
    try {
      const resp = await axios.get(`${API}/hints/polygon/${panorama.id}`);
      if (resp.data && resp.data.points && resp.data.points.length >= 3) {
        setHintPolygonPoints(resp.data.points.map(p => [p.lat, p.lng]));
        setHintPolygonClosed(true);
        setHintPolygonIsNew(false);
      }
      if (resp.data && resp.data.hint_cost !== undefined) {
        setHintPolygonCost(resp.data.hint_cost);
      }
    } catch (e) {}
  };

  const closeHintPolygon = () => {
    if (hintPolygonPoints.length >= 3) {
      setHintPolygonClosed(true);
      setHintPolygonIsNew(true);
    }
  };

  const cancelHintDrawing = () => {
    setHintPolygonPoints([]);
    setHintPolygonClosed(false);
    setHintPolygonIsNew(false);
  };

  const removeLastHintPoint = () => {
    if (hintPolygonPoints.length > 0) {
      setHintPolygonPoints(prev => prev.slice(0, -1));
      if (hintPolygonClosed) setHintPolygonClosed(false);
    }
  };

  const saveHintPolygon = async () => {
    if (hintPolygonPoints.length < 3 && !hintPolygonExisting[hintPolygonEditor]) return;
    setHintPolygonSaving(true);
    try {
      await axios.post(`${API}/hints/polygon/${hintPolygonEditor}`, {
        points: hintPolygonPoints.map(p => ({ lat: p[0], lng: p[1] })),
        hint_cost: hintPolygonCost || 0
      }, { headers: getAuthHeader() });
      setHintPolygonExisting(prev => ({ ...prev, [hintPolygonEditor]: true }));
      setHintPolygonEditor(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setHintPolygonSaving(false);
    }
  };

  const deleteHintPolygon = async () => {
    if (!window.confirm('Удалить полигон подсказки?')) return;
    try {
      await axios.delete(`${API}/hints/polygon/${hintPolygonEditor}`, { headers: getAuthHeader() });
      setHintPolygonExisting(prev => ({ ...prev, [hintPolygonEditor]: false }));
      setHintPolygonEditor(null);
      setHintPolygonPoints([]);
      setHintPolygonClosed(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleDeleteGame = async () => {
    const panoramaCount = panoramas.length;
    const confirmMessage = panoramaCount > 0 
      ? `Вы уверены, что хотите удалить игру "${game.name}" и все ${panoramaCount} панорам(ы)? Это действие нельзя отменить.`
      : `Вы уверены, что хотите удалить игру "${game.name}"?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingGame(true);
    try {
      await axios.delete(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      toast.success(t('gamePageUI.gameDeleted'));
      navigate('/');
    } catch (error) {
      console.error('Error deleting game:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления игры');
      setDeletingGame(false);
    }
  };

  // Announce game to a single channel or all channels at once
  const handleAnnounce = async (channel) => {
    setSendingAll(true);
    const labels = { telegram: 'Telegram', max: 'MAX', vk: 'VK' };
    try {
      if (channel === 'all') {
        const r = await axios.post(`${API}/games/${gameId}/announce-all`, {}, {
          headers: getAuthHeader()
        });
        const res = r.data || {};
        const lines = ['telegram', 'max', 'vk'].map(ch => {
          const c = res[ch] || {};
          return c.ok ? `✅ ${labels[ch]}: отправлено` : `❌ ${labels[ch]}: ${c.error || 'ошибка'}`;
        });
        alert(lines.join('\n'));
      } else {
        await axios.post(`${API}/games/${gameId}/announce-${channel}`, {}, {
          headers: getAuthHeader()
        });
        alert(`✅ ${labels[channel]}: анонс успешно отправлен`);
      }
      setAnnRefreshKey(k => k + 1);
    } catch (error) {
      console.error('Error announcing:', error);
      alert(`❌ ${labels[channel] || ''}: ` + (error.response?.data?.detail || 'Не удалось отправить анонс'));
    } finally {
      setSendingAll(false);
    }
  };

  // Delete user result
  const handleDeleteResult = async (userId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/results/${userId}`, {
        headers: getAuthHeader()
      });
      
      toast.success(t('gamePageUI.resultWasSuccessfullyDeleted'));
      
      // Refresh results and winners
      await fetchGameData();
    } catch (error) {
      console.error('Error deleting result:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления результата');
      throw error;
    }
  };

  // Drag and drop handlers for reordering panoramas
  const handlePanoramaDragStart = (e, panorama, index) => {
    if (!hasRole(['admin', 'editor'])) return;
    setDraggedPanorama({ panorama, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handlePanoramaDragOver = (e, index) => {
    if (!hasRole(['admin', 'editor']) || !draggedPanorama) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handlePanoramaDragLeave = () => {
    setDragOverIndex(null);
  };

  const handlePanoramaDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (!hasRole(['admin', 'editor']) || !draggedPanorama) return;

    const dragIndex = draggedPanorama.index;
    if (dragIndex === dropIndex) {
      setDraggedPanorama(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder panoramas array
    const newPanoramas = [...panoramas];
    const [removed] = newPanoramas.splice(dragIndex, 1);
    newPanoramas.splice(dropIndex, 0, removed);

    setPanoramas(newPanoramas);
    setDraggedPanorama(null);
    setDragOverIndex(null);

    // Update order on backend
    try {
      await axios.put(`${API}/games/${gameId}/panoramas/reorder`, 
        { panorama_ids: newPanoramas.map(p => p.id) },
        { headers: getAuthHeader() }
      );
      toast.success(t('gamePageUI.orderPanoramasBeenUpdated'));
    } catch (error) {
      console.error('Error updating panorama order:', error);
      toast.error(t('gamePageUI.orderUpdateError'));
      // Revert on error
      fetchGameData();
    }
  };

  const handlePanoramaDragEnd = () => {
    setDraggedPanorama(null);
    setDragOverIndex(null);
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        toast.error(t('gamePageUI.pleaseSelectVideoFile'));
        return;
      }
      setVideoFile(file);
    }
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      toast.error('Пожалуйста, выберите видео файл');
      return;
    }
    
    const fileSizeMb = (file.size / (1024 * 1024)).toFixed(1);
    setVideoFileInfo({ name: file.name, size: file.size, sizeMb: fileSizeMb });
    setUploadingVideo(true);
    setVideoUploadProgress(0);
    setVideoUploadSpeed(0);
    setVideoUploadEta(0);
    setVideoProcessingInfo(null);
    
    try {
      const CHUNK_SIZE = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadStartTime = Date.now();
      
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('chunk_index', chunkIndex.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('filename', file.name);
        
        await axios.post(`${API}/games/${gameId}/video-chunk`, formData, {
          headers: { ...getAuthHeader() }
        });
        
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setVideoUploadProgress(progress);
        
        const elapsedSec = (Date.now() - uploadStartTime) / 1000;
        const uploadedBytes = (chunkIndex + 1) * CHUNK_SIZE;
        const speedMbps = (uploadedBytes / (1024 * 1024)) / elapsedSec;
        setVideoUploadSpeed(speedMbps);
        
        const remainingBytes = file.size - uploadedBytes;
        const etaSec = speedMbps > 0 ? (remainingBytes / (1024 * 1024)) / speedMbps : 0;
        setVideoUploadEta(Math.max(0, Math.round(etaSec)));
      }
      
      // Finalize
      setVideoUploadProgress(100);
      setVideoProcessingInfo({ step: 'Объединение файлов...', progress: 0 });
      
      await axios.post(`${API}/games/${gameId}/video-finalize`, {
        total_chunks: totalChunks,
        filename: file.name
      }, { headers: getAuthHeader() });
      
      // Start polling for processing status
      setVideoProcessingInfo({ step: 'Запуск обработки HLS...', progress: 0 });
      
      const pollProcessing = async () => {
        const maxPolls = 600; // 10 min max
        for (let i = 0; i < maxPolls; i++) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const statusResp = await axios.get(`${API}/games/${gameId}/video/status?t=${Date.now()}`);
            const st = statusResp.data;
            
            setVideoProcessingInfo({
              step: st.processing_step || 'Обработка...',
              progress: st.processing_progress || 0,
              etaSec: st.processing_eta_sec || 0,
              elapsedSec: st.processing_elapsed_sec || 0,
              fileSizeMb: st.video_file_size_mb || 0,
              durationSec: st.video_duration_sec || 0
            });
            
            if (st.hls_ready || (!st.processing && st.processing_progress >= 100)) {
              break;
            }
            if (!st.processing && st.processing_progress === 0 && i > 5) {
              break; // Processing probably failed
            }
          } catch (e) {
            console.error('Error polling processing status:', e);
          }
        }
      };
      
      await pollProcessing();
      
      setVideoFile(null);
      const gameResponse = await axios.get(`${API}/games/${gameId}`, { headers: getAuthHeader() });
      setGame(gameResponse.data);
      toast.success(t('gamePageUI.videoBeenSuccessfullyProcessed'));
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.response?.data?.detail || 'Ошибка загрузки видео');
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(0);
      setVideoProcessingInfo(null);
      setVideoFileInfo(null);
    }
  };

  // Old handleVideoUpload function removed - now using handleVideoUploadWithFile

  const handleVideoDelete = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить видео?')) return;
    
    setUploadingVideo(true);
    try {
      // Delete video cover if exists
      if (game.video_cover_path) {
        try {
          await axios.delete(`${API}/games/${gameId}/video-cover`, {
            headers: getAuthHeader()
          });
        } catch (error) {
          console.log('No cover to delete or already deleted');
        }
      }
      
      // Delete video
      await axios.delete(`${API}/games/${gameId}/video`, {
        headers: getAuthHeader()
      });
      
      // Reload only game info to update video_path
      const gameResponse = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setGame(gameResponse.data);
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error(t('gamePageUI.videoDeletionError'));
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleVideoCoverSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error(t('gamePageUI.pleaseSelectImage'));
      return;
    }
    
    setUploadingVideoCover(true);
    
    try {
      const formData = new FormData();
      formData.append('cover', file);
      
      await axios.post(`${API}/games/${gameId}/video-cover`, formData, {
        headers: {
          ...getAuthHeader(),
          
        }
      });
      
      // Reload game info
      const gameResponse = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setGame(gameResponse.data);
    } catch (error) {
      console.error('Error uploading video cover:', error);
      toast.error(error.response?.data?.detail || 'Ошибка загрузки обложки');
    } finally {
      setUploadingVideoCover(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleVideoCoverDelete = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить обложку видео?')) return;
    
    setUploadingVideoCover(true);
    try {
      await axios.delete(`${API}/games/${gameId}/video-cover`, {
        headers: getAuthHeader()
      });
      
      // Reload game info
      const gameResponse = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setGame(gameResponse.data);
    } catch (error) {
      console.error('Error deleting video cover:', error);
      toast.error(t('gamePageUI.errorDeletingCover'));
    } finally {
      setUploadingVideoCover(false);
    }
  };

  const handlePrizeVideoCoverUpload = async (place, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }
    
    const key = `${place}-video-cover`;
    setUploadingPrize(key);
    
    try {
      const formData = new FormData();
      formData.append('cover', file);
      
      await axios.post(`${API}/games/${gameId}/prize/${place}/video-cover`, formData, {
        headers: {
          ...getAuthHeader(),
          
        }
      });
      
      // Reload game data
      const gameResponse = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setGame(gameResponse.data);
    } catch (error) {
      console.error('Error uploading prize video cover:', error);
      toast.error(error.response?.data?.detail || 'Ошибка загрузки обложки');
    } finally {
      setUploadingPrize(null);
      e.target.value = ''; // Reset input
    }
  };

  const handlePrizeVideoCoverDelete = async (place) => {
    if (!window.confirm('Вы уверены, что хотите удалить обложку?')) return;
    
    const key = `${place}-video-cover`;
    setUploadingPrize(key);
    
    try {
      await axios.delete(`${API}/games/${gameId}/prize/${place}/video-cover`, {
        headers: getAuthHeader()
      });
      
      // Reload game data
      const gameResponse = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setGame(gameResponse.data);
    } catch (error) {
      console.error('Error deleting prize video cover:', error);
      toast.error('Ошибка удаления обложки');
    } finally {
      setUploadingPrize(null);
    }
  };

  const handlePrizeUpload = async (place, mediaType, file) => {
    const key = `${place}-${mediaType}`;
    setUploadingPrize(key);
    
    try {
      // For images, use simple upload
      if (mediaType === 'image') {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(`${API}/games/${gameId}/prize/${place}/${mediaType}`, formData, {
          headers: {
            ...getAuthHeader(),
            
          }
        });
      } else {
        // For videos, use chunked upload with progress tracking
        const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        
        setPrizeVideoProgress(prev => ({ ...prev, [place]: 0 }));
        
        // Upload chunks
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          
          const formData = new FormData();
          formData.append('chunk', chunk);
          formData.append('chunk_index', i);
          formData.append('total_chunks', totalChunks);
          formData.append('filename', file.name);
          
          await axios.post(`${API}/games/${gameId}/prize/${place}/video-chunk`, formData, {
            headers: {
              ...getAuthHeader(),
              
            }
          });
          
          const progress = Math.round(((i + 1) / totalChunks) * 100);
          setPrizeVideoProgress(prev => ({ ...prev, [place]: progress }));
        }
        
        // Finalize upload
        setPrizeVideoProgress(prev => ({ ...prev, [place]: 100 }));
        await axios.post(`${API}/games/${gameId}/prize/${place}/video-finalize`, {
          total_chunks: totalChunks,
          filename: file.name
        }, {
          headers: getAuthHeader()
        });
      }
      
      // Reload game data
      const gameResponse = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setGame(gameResponse.data);
    } catch (error) {
      console.error('Error uploading prize:', error);
      toast.error(error.response?.data?.detail || 'Ошибка загрузки приза');
    } finally {
      setUploadingPrize(null);
      setPrizeVideoProgress(prev => ({ ...prev, [place]: 0 }));
    }
  };

  const handlePrizeDelete = async (place, mediaType) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот приз?')) return;
    
    const key = `${place}-${mediaType}`;
    setUploadingPrize(key);
    
    try {
      // If deleting video, also delete its cover if exists
      if (mediaType === 'video') {
        const place_names = {1: 'first', 2: 'second', 3: 'third'};
        const coverField = `${place_names[place]}_prize_video_cover`;
        if (game[coverField]) {
          try {
            await axios.delete(`${API}/games/${gameId}/prize/${place}/video-cover`, {
              headers: getAuthHeader()
            });
          } catch (error) {
            console.log('No cover to delete or already deleted');
          }
        }
      }
      
      await axios.delete(`${API}/games/${gameId}/prize/${place}/${mediaType}`, {
        headers: getAuthHeader()
      });
      
      // Reload game data
      const gameResponse = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setGame(gameResponse.data);
    } catch (error) {
      console.error('Error deleting prize:', error);
      toast.error(t('gamePageUI.prizeDeletionError'));
    } finally {
      setUploadingPrize(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-state" style={{ minHeight: '100vh' }}>
        <Loader2 className="animate-spin" size={40} />
        <p>{t('gamePageUI.loadingGame')}</p>
      </div>
    );
  }

  if (!game) {
    return null;
  }

  return (
    <>
    <div 
      className="gallery-container"
      style={{ position: 'relative' }}
    >
      {/* Game Winners medal strip (hidden for objects_only games — no winners there) */}
      {gameId && game?.game_mode !== 'objects_only' && (
        <GameWinnersStrip
          gameId={gameId}
          currentUserId={user?.id}
          variant="bare"
          anchor={{ position: 'fixed', top: '58px', right: '0.75rem', zIndex: 9990 }}
        />
      )}

      {/* Drag and Drop Overlay - removed */}
      {false && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(102, 126, 234, 0.95)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '2rem',
          fontWeight: 600,
          pointerEvents: 'none'
        }}>
          <Upload size={80} style={{ marginBottom: '1rem' }} />
          <div>{t('gamePageUI.dragDropDirectoriesPanoramas')}</div>
          <div style={{ fontSize: '1.2rem', marginTop: '0.5rem', opacity: 0.9 }}>
            {t('gamePageUI.canUploadMultipleDirectories')}
          </div>
        </div>
      )}

      {/* Upload Progress Overlay */}
      {uploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}>
          <Loader2 className="animate-spin" size={60} style={{ marginBottom: '1.5rem' }} />
          <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
            {t('gamePageUI.loadingPanoramas')}
          </div>
          {uploadProgress.total > 0 && (
            <div style={{ fontSize: '1.2rem', marginTop: '1rem', opacity: 0.9 }}>
              {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
            </div>
          )}
        </div>
      )}
      {/* Game Header */}
      <div className="game-admin-header" style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button variant="outline" onClick={() => navigate('/')} title={t('gamePageUI.goMainPage')}>
            <Home size={18} />
          </Button>
          {/* "All Answers on Map" button moved to a clickable card between
              cover and accordion — see "All Answers Card" section below. */}
          {/* Publication status now shown via Eye/EyeOff icon only */}
          {hasRole(['admin', 'editor']) && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/games/${gameId}/edit`)}
                disabled={deletingGame}
              >
                <Edit size={16} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePublishToggle}
                disabled={deletingGame}
                title={game.published ? 'Опубликовано - нажмите чтобы снять с публикации' : 'Не опубликовано - нажмите чтобы опубликовать'}
                style={{
                  background: game.published ? '#22c55e' : 'transparent',
                  color: game.published ? 'white' : 'inherit',
                  borderColor: game.published ? '#22c55e' : undefined
                }}
              >
                {game.published ? <Eye size={16} /> : <EyeOff size={16} />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingCover}
                title={t('gamePageUI.uploadGameCover')}
                style={{
                  background: game.cover_image_path ? '#667eea' : 'transparent',
                  color: game.cover_image_path ? 'white' : 'inherit',
                  borderColor: game.cover_image_path ? '#667eea' : undefined
                }}
              >
                {uploadingCover ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => jpgInputRef.current?.click()}
                disabled={uploading}
                title={t('gamePageUI.addPanorama')}
              >
                {uploading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <ImagePlus size={16} />
                )}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={sendingAll}
                    title={t('gamePageUI.announceAll')}
                    data-testid="announce-menu-btn"
                    style={{
                      background: '#16a34a',
                      color: 'white',
                      borderColor: '#16a34a',
                    }}
                  >
                    {sendingAll ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Megaphone size={16} />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem data-testid="announce-telegram-item" onClick={() => handleAnnounce('telegram')}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#0088cc', marginRight: 8 }} />
                    Telegram
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="announce-max-item" onClick={() => handleAnnounce('max')}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#6C47FF', marginRight: 8 }} />
                    MAX
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="announce-vk-item" onClick={() => handleAnnounce('vk')}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#0077FF', marginRight: 8 }} />
                    VK
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="announce-all-item" onClick={() => handleAnnounce('all')} style={{ fontWeight: 600 }}>
                    <Megaphone size={14} style={{ marginRight: 8 }} />
                    Во все каналы
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteGame}
                disabled={deletingGame}
                style={{
                  color: '#dc2626',
                  borderColor: '#dc2626'
                }}
              >
                {deletingGame ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
              </Button>
            </>
          )}
        </div>

      </div>

      {hasRole(['admin', 'editor']) && (
        <AnnouncementStats gameId={gameId} refreshKey={annRefreshKey} />
      )}

      {/* Upload Buttons - removed as requested, now handled by icon near delete button */}
      <input
        ref={coverInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleCoverUpload}
        style={{ display: "none" }}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        webkitdirectory=""
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
      <input
        ref={zipInputRef}
        type="file"
        accept=".zip"
        onChange={handleZipSelect}
        style={{ display: "none" }}
      />
      <input
        ref={jpgInputRef}
        type="file"
        accept=".jpg,.jpeg"
        multiple
        onChange={handleJpgSelect}
        style={{ display: "none" }}
      />

      {/* Game Title - using same wrapper class as accordion */}
      <div className="wrapper" style={{ marginBottom: '0.25rem', marginTop: '4rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h1 style={{
            fontSize: '1.67rem',
            fontWeight: 'bold',
            color: '#1a1a1a',
            textAlign: 'left',
            margin: 0
          }}>
            {getTranslatedText(game.name_translations, game.name)}
          </h1>
          {/* Paid Game Badge - hide for collecting games since info is in registration block */}
          {game.points_required > 0 && game.game_status !== 'collecting' && (
            <div style={{
              padding: '0.5rem 0.75rem',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '8px',
              border: '1px solid #f59e0b',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 6px rgba(245, 158, 11, 0.15)'
            }}>
              <span style={{ fontSize: '1.2rem' }}>⭐</span>
              <div>
                <div style={{ fontWeight: '600', color: '#92400e', fontSize: '0.85rem' }}>
                  {t('gamePageUI.playingPoints')}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#b45309' }}>
                  Стоимость: {game.points_required} баллов
                </div>
              </div>
            </div>
          )}
          {/* Game Status Badge - only for non-collecting games */}
          {game.game_status && game.game_status !== 'active' && game.game_status !== 'draft' && game.game_status !== 'collecting' && (
            <span style={{
              padding: '0.4rem 0.75rem',
              background: game.game_status === 'cancelled' ? '#ef4444' 
                : game.game_status === 'completed' ? '#22c55e' : '#6b7280',
              color: 'white',
              borderRadius: '6px',
              fontWeight: '500',
              fontSize: '0.85rem'
            }}>
              {game.game_status === 'cancelled' ? '❌ Отменена' 
                : game.game_status === 'completed' ? '✅ Завершена' 
                : game.game_status}
            </span>
          )}
          {/* Countdown to game start - only for non-collecting games */}
          {game.registration_deadline && countdown && !countdown.expired && game.game_status !== 'collecting' && (
            <div style={{
              padding: '0.5rem 0.75rem',
              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
              borderRadius: '8px',
              border: '1px solid #3b82f6',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1rem' }}>⏱️</span>
              <div>
                <div style={{ fontWeight: '600', color: '#1e40af', fontSize: '0.85rem' }}>
                  {t('gamePageUI.beforeGameStarts')}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#1e3a8a', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {countdown.days > 0 && `${countdown.days}д `}
                  {String(countdown.hours).padStart(2, '0')}:
                  {String(countdown.minutes).padStart(2, '0')}:
                  {String(countdown.seconds).padStart(2, '0')}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Game start date/time info - hide for collecting games since shown in registration block */}
        {game.registration_deadline && game.game_status !== 'collecting' && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
            📅 Начало игры: {new Date(game.registration_deadline).toLocaleString('ru-RU', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}
      </div>

      {/* Registration Block for Collecting Games */}
      {game.game_status === 'collecting' && (
        <div className="wrapper" style={{
          padding: '1.25rem',
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          borderRadius: '12px',
          border: '2px solid #3b82f6',
          boxSizing: 'border-box',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e40af', fontSize: '1.1rem' }}>
            📋 Регистрация на игру. Начало {new Date(game.registration_deadline).toLocaleString('ru-RU', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })} в {new Date(game.registration_deadline).toLocaleString('ru-RU', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </h3>
          
          {/* Cost info */}
          <div style={{ marginBottom: '1rem', fontSize: '0.95rem', color: '#1e40af' }}>
            {t('gamePageUI.participationFee')} <strong>{game.points_required} баллов</strong>
            {game.min_participants > 0 && (
              <span style={{ marginLeft: '1rem' }}>
                {t('gamePageUI.requiredNumberParticipants')} <strong>{game.min_participants}</strong>
              </span>
            )}
          </div>
          
          {/* Countdown Timer */}
          {countdown && !countdown.expired && (
            <div style={{
              background: 'white',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>⏱️</span>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>{t('gamePageUI.untilEndRegistration')}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e40af' }}>
                  {countdown.days > 0 && `${countdown.days} дн. `}
                  {String(countdown.hours).padStart(2, '0')}:{String(countdown.minutes).padStart(2, '0')}:{String(countdown.seconds).padStart(2, '0')}
                </div>
              </div>
            </div>
          )}
          {countdown?.expired && (
            <div style={{
              background: '#fef2f2',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              color: '#dc2626',
              fontWeight: '500'
            }}>
              {t('gamePageUI.registrationTimeExpired')}
            </div>
          )}
          
          {/* Progress Bar */}
          {game.min_participants > 0 && registrationInfo && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
                fontSize: '0.9rem'
              }}>
                <span>{t('gamePageUI.participants')}</span>
                <span style={{ fontWeight: '600' }}>
                  {registrationInfo.registered_count} / {game.min_participants}
                  {registrationInfo.registered_count >= game.min_participants && ' ✓'}
                </span>
              </div>
              <div style={{
                background: '#e2e8f0',
                borderRadius: '6px',
                height: '12px',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: registrationInfo.registered_count >= game.min_participants 
                    ? 'linear-gradient(90deg, #22c55e, #16a34a)' 
                    : 'linear-gradient(90deg, #3b82f6, #2563eb)',
                  height: '100%',
                  width: `${Math.min(100, (registrationInfo.registered_count / game.min_participants) * 100)}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              {registrationInfo.registered_count < game.min_participants && (
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                  Нужно ещё {game.min_participants - registrationInfo.registered_count} участников для старта игры
                </p>
              )}
            </div>
          )}
          
          {/* Registration Actions */}
          {user ? (
            registrationInfo?.user_registered ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '0.5rem 1rem',
                  background: '#dcfce7',
                  color: '#166534',
                  borderRadius: '8px',
                  fontWeight: '600'
                }}>
                  {t('gamePageUI.registered')}
                </span>
                {!countdown?.expired && (
                  <button
                    onClick={handleUnregisterFromGame}
                    disabled={registering}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'white',
                      color: '#dc2626',
                      border: '1px solid #dc2626',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {registering ? 'Отмена...' : 'Отменить регистрацию'}
                  </button>
                )}
              </div>
            ) : (
              <div>
                {/* Warning about refund if not enough participants */}
                {game.fallback_to_free && (
                  <p style={{ 
                    fontSize: '0.85rem', 
                    color: '#6b7280', 
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    {t('gamePageUI.ifMinimumNumberParticipants')}
                  </p>
                )}
                {!game.fallback_to_free && (
                  <p style={{ 
                    fontSize: '0.85rem', 
                    color: '#6b7280', 
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    {t('gamePageUI.ifMinimumNumberParticipants2')}
                  </p>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={handleRegisterForGame}
                    disabled={registering || countdown?.expired || (registrationInfo?.user_points || 0) < game.points_required}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: countdown?.expired || (registrationInfo?.user_points || 0) < game.points_required 
                        ? '#9ca3af' 
                        : 'linear-gradient(135deg, #22c55e, #16a34a)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '1rem',
                      cursor: countdown?.expired || (registrationInfo?.user_points || 0) < game.points_required 
                        ? 'not-allowed' 
                        : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {registering ? 'Регистрация...' : `⭐ Участвовать за ${game.points_required} баллов`}
                  </button>
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>
                    Ваш баланс: {Math.round(registrationInfo?.user_points || 0)} баллов
                  </span>
                </div>
              </div>
            )
          ) : (
            <p style={{ color: '#666' }}>
              <a href="/game/login" style={{ color: '#3b82f6', fontWeight: '500' }}>{t('gamePageUI.come')}</a>{t('gamePageUI.registerGame')}
            </p>
          )}
        </div>
      )}

      {/* Old activation system - for backwards compatibility with non-collecting paid games */}
      {game.points_required > 0 && game.game_status !== 'collecting' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {/* Activate Button */}
          {user && !hasRole(['admin', 'editor']) && !gameActivated && (
            <button
              onClick={handleActivateGame}
              disabled={activatingGame || (user && userPoints < game.points_required)}
              style={{
                padding: '0.5rem 1rem',
                background: userPoints >= game.points_required ? '#22c55e' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.9rem',
                cursor: userPoints >= game.points_required ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {activatingGame ? 'Активация...' : 'Активировать'}
            </button>
          )}
          {/* Activated Badge */}
          {gameActivated && !hasRole(['admin', 'editor']) && (
            <span style={{
              padding: '0.5rem 0.75rem',
              background: '#dcfce7',
              color: '#166534',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '0.85rem',
              border: '1px solid #22c55e'
            }}>
              {t('gamePageUI.activated')}
            </span>
          )}
        </div>
      )}

      {/* Game Cover Image OR Video Player (replaces cover when game has video) */}
      <div className="wrapper">
      {(() => {
        const hasVideo = !!(game.video_path || game.video_hls_ready || game.external_video_url);
        if (hasVideo) {
          // Video games: show the InteractiveGamePlayer in place of the cover
          return (
            <>
              <div
                className="game-fullbleed-media"
                data-testid="cover-slot-video-player"
                style={{
                  position: 'relative',
                  marginBottom: videoTimecodes.length > 0 ? '0.75rem' : '1.5rem',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  background: '#000'
                }}
              >
                <InteractiveGamePlayer
                  gameId={gameId}
                  poster={game.video_cover_path ? `${API}/games/${gameId}/video-cover?t=${Date.now()}` : (game.cover_image_path ? `${API}/games/${gameId}/cover` : undefined)}
                  style={{
                    width: '100%',
                    maxHeight: '600px',
                    display: 'block'
                  }}
                  getAuthHeader={getAuthHeader}
                  coverOnly={true}
                />
              </div>

              {/* Video Timecodes (chips) — anchored right under the player */}
              {videoTimecodes.length > 0 && (game.video_path || game.external_video_url) && (
                <div data-testid="cover-slot-timecodes" style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  borderRadius: '12px',
                  border: '1px solid #fbbf24'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#92400e',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Play size={18} />
                    {t('gamePageUI.videoTimecodes')}
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {videoTimecodes.map((tc) => {
                      const mins = Math.floor(tc.timecode / 60);
                      const secs = Math.floor(tc.timecode % 60);
                      const timeStr = `${mins}:${String(secs).padStart(2, '0')}`;
                      return (
                        <button
                          key={tc.id}
                          onClick={() => {
                            const videoUrl = game.video_path
                              ? `${API}/games/${gameId}/video/hls/master.m3u8`
                              : game.external_video_url;
                            if (isVideoActive) {
                              seek(tc.timecode);
                            } else {
                              startVideoAtTime(gameId, videoUrl, tc.timecode);
                            }
                          }}
                          style={{
                            background: 'white',
                            border: '1px solid #d97706',
                            borderRadius: '20px',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.85rem',
                            color: '#92400e',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => { e.target.style.background = '#fef3c7'; e.target.style.transform = 'scale(1.05)'; }}
                          onMouseOut={(e) => { e.target.style.background = 'white'; e.target.style.transform = 'scale(1)'; }}
                        >
                          <span style={{ fontWeight: 600 }}>{timeStr}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          );
        }
        // No-video games: keep the existing cover-image behaviour
        if (!game.cover_image_path) return null;
        return (
          <>
            {isVideoActive && mode === 'embedded' ? (
              <div
                className="game-fullbleed-media"
                style={{
                  marginBottom: '1.5rem',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: '#000',
                  aspectRatio: '16/9'
                }}
              />
            ) : (
              <div
                className="game-fullbleed-media"
                style={{
                  marginBottom: '1.5rem',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  position: 'relative'
                }}
              >
                <img src={`${API}/games/${gameId}/cover`} alt={getTranslatedText(game.name_translations, game.name)} style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>
            )}
          </>
        );
      })()}
      </div>

      {/* All Answers on Map — inline card (replaces previous header button).
          Visible only for authenticated users who have made at least one guess.
          Wrapped in `.wrapper` so its width matches the accordion below. */}
      {user && myResults && (myResults.answered_count || 0) > 0 && (
        <div className="wrapper">
          <div
            role="button"
            tabIndex={0}
            data-testid="all-answers-card"
            onClick={() => setShowAllAnswersMap(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowAllAnswersMap(true);
              }
            }}
            style={{
              margin: '0 0 1.5rem',
              padding: '14px 18px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0a4797 0%, #1e5fb8 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(10, 71, 151, 0.25)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              userSelect: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(10, 71, 151, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(10, 71, 151, 0.25)';
            }}
          >
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <MapIcon size={22} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '16px', fontWeight: 600, lineHeight: 1.3 }}>
                Все ответы на одной карте
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9, marginTop: '2px' }}>
                {myResults.answered_count} из {myResults.total_panoramas || panoramas.length || 0} ответов сделано
              </div>
            </div>
            <div style={{ opacity: 0.7, flexShrink: 0 }}>
              <ArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
            </div>
          </div>
        </div>
      )}

      {/* Video Stats Widget — admin/editor only */}
      {(game.video_path || game.video_hls_ready || game.external_video_url) && hasRole(['admin', 'editor']) && (
        <VideoStatsWidget gameId={gameId} />
      )}

      {/* Full Video Analytics (Admin/Editor only) */}
      {(game.video_path || game.external_video_url) && hasRole(['admin', 'editor']) && (
        <GameVideoAnalytics 
          gameId={gameId} 
          getAuthHeader={getAuthHeader}
          videoDuration={game.video_duration || 300}
          panoramas={panoramas}
        />
      )}

      {/* Accordion Wrapper */}
      <Accordion 
        defaultOpen={[0]}
        sections={(() => {
          const sections = [
          // Section 1: Game Info & Video
          {
            title: t('game.description'),
            content: (
              <>
      {/* Game Description */}
      {(game.description_translations?.[language] || game.description_translations?.['ru'] || game.description) && (
        <div style={{ 
          marginBottom: '1.5rem',
          padding: '1rem',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            {getTranslatedText(game.description_translations, game.description)}
          </p>
        </div>
      )}

      {/* Video Timecodes Links — hidden when shown at the top (with the player) */}
      {false && videoTimecodes.length > 0 && (game.video_path || game.external_video_url) && (
        <div style={{ 
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderRadius: '12px',
          border: '1px solid #fbbf24'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: 600, 
            color: '#92400e', 
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Play size={18} />
            {t('gamePageUI.videoTimecodes')}
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {videoTimecodes.map((tc, idx) => {
              const mins = Math.floor(tc.timecode / 60);
              const secs = Math.floor(tc.timecode % 60);
              const timeStr = `${mins}:${String(secs).padStart(2, '0')}`;
              
              
              return (
                <button
                  key={tc.id}
                  onClick={() => {
                    const videoUrl = game.video_path 
                      ? `${API}/games/${gameId}/video/hls/master.m3u8`
                      : game.external_video_url;
                    if (isVideoActive) {
                      seek(tc.timecode);
                    } else {
                      startVideoAtTime(gameId, videoUrl, tc.timecode);
                    }
                  }}
                  style={{
                    background: 'white',
                    border: '1px solid #d97706',
                    borderRadius: '20px',
                    padding: '0.4rem 0.8rem',
                    fontSize: '0.85rem',
                    color: '#92400e',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#fef3c7';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{timeStr}</span>
                  
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Hotspots Section - Objects to find */}
      {allHotspots.length > 0 && (
        <div style={{ 
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '12px',
          border: '1px solid #bae6fd'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: 600, 
            color: '#0369a1', 
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Target size={18} />
            {t('gamePageUI.additionalPointsIfFind')}
          </h4>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '0.5rem' 
          }}>
            {allHotspots.map((hs, idx) => {
              const isFound = foundHotspotIds.includes(hs.id);
              return (
                <span 
                  key={hs.id || idx}
                  style={{
                    background: isFound ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'white',
                    padding: '0.375rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    color: isFound ? 'white' : '#334155',
                    boxShadow: isFound ? '0 2px 4px rgba(16, 185, 129, 0.3)' : '0 1px 2px rgba(0,0,0,0.05)',
                    border: isFound ? '1px solid #10b981' : '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  {isFound && <span>✓</span>}
                  {getTranslatedText(hs.name_translations, hs.name)}
                </span>
              );
            })}
          </div>
          {user && foundHotspotIds.length > 0 && (
            <div style={{ 
              marginTop: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}>
              <p style={{ 
                fontSize: '0.8rem', 
                color: '#059669',
                fontWeight: 500,
                margin: 0
              }}>
                {t('game.foundOf', { found: foundHotspotIds.length, total: allHotspots.length })}
              </p>
              {hasRole(['admin', 'editor']) && (
                <button
                  onClick={handleResetHotspots}
                  style={{
                    background: 'transparent',
                    border: '1px solid #ef4444',
                    color: '#ef4444',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#ef4444';
                    e.target.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#ef4444';
                  }}
                >
                  {t('gamePageUI.resetResults')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Game Video Section */}
      <div style={{ marginBottom: '2rem' }}>
        {/* Video Upload Interface OR Video Player */}
        {!game.video_path && !game.video_hls_ready && !game.external_video_url && !uploadingVideo && hasRole(['admin', 'editor']) ? (
          /* Upload/Link options when no video exists */
          <div style={{
            marginBottom: '1rem',
            padding: '1.5rem',
            background: '#f8f9fa',
            borderRadius: '12px',
            border: '2px dashed #cbd5e0'
          }}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Upload button */}
              <div>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleVideoUpload(file);
                      e.target.value = '';
                    }
                  }}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => videoInputRef.current?.click()}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Upload size={18} />
                  {t('gamePageUI.uploadVideo')}
                </button>
              </div>
              
              <div style={{ color: '#9ca3af', alignSelf: 'center', fontWeight: 500 }}>{t('gamePageUI.or')}</div>
              
              {/* External video URL input */}
              <div style={{ flex: 1, minWidth: '250px' }}>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const input = e.target.elements.externalVideoUrl;
                  const url = input.value.trim();
                  if (!url) return;
                  
                  try {
                    setUploadingVideo(true);
                    const response = await axios.post(
                      `${API}/games/${gameId}/external-video`,
                      { url },
                      { headers: getAuthHeader() }
                    );
                    if (response.data.success) {
                      await fetchGameData();
                      input.value = '';
                    }
                  } catch (err) {
                    alert('Ошибка: ' + (err.response?.data?.detail || err.message));
                  } finally {
                    setUploadingVideo(false);
                  }
                }} style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    name="externalVideoUrl"
                    type="url"
                    placeholder={t('gamePageUI.insertLinkVkvideoYoutube')}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.95rem'
                    }}
                  />
                  <button
                    type="submit"
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {t('gamePageUI.add')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        ) : uploadingVideo ? (
          /* Uploading/Processing state */
          <div style={{
            width: '100%',
            minHeight: '300px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#0f172a',
            borderRadius: '12px',
            color: '#fff',
            padding: '2rem'
          }}>
            {videoProcessingInfo ? (
              /* === HLS Processing Phase === */
              <>
                <div style={{ 
                  width: '56px', height: '56px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '1rem', position: 'relative'
                }}>
                  <Loader2 className="animate-spin" size={28} style={{ color: '#fff' }} />
                  {videoProcessingInfo.progress > 0 && videoProcessingInfo.progress < 100 && (
                    <svg style={{ position: 'absolute', top: -3, left: -3, width: 62, height: 62, transform: 'rotate(-90deg)' }}>
                      <circle cx="31" cy="31" r="28" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
                      <circle cx="31" cy="31" r="28" fill="none" stroke="#a5b4fc" strokeWidth="3"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - videoProcessingInfo.progress / 100)}`}
                        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                      />
                    </svg>
                  )}
                </div>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem', color: '#e0e7ff' }}>
                  Обработка видео
                </p>
                <p style={{ fontSize: '0.95rem', marginBottom: '1rem', color: '#a5b4fc' }}>
                  {videoProcessingInfo.step}
                </p>
                {/* Main progress bar */}
                <div style={{
                  width: '85%', maxWidth: '450px', height: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '5px', overflow: 'hidden', marginBottom: '0.75rem'
                }}>
                  <div style={{
                    width: `${videoProcessingInfo.progress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #6366f1, #a78bfa)',
                    borderRadius: '5px',
                    transition: 'width 1s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', color: '#94a3b8' }}>
                  <span>{videoProcessingInfo.progress}%</span>
                  {videoProcessingInfo.elapsedSec > 0 && (
                    <span>Прошло: {Math.floor(videoProcessingInfo.elapsedSec / 60)}:{String(videoProcessingInfo.elapsedSec % 60).padStart(2, '0')}</span>
                  )}
                  {videoProcessingInfo.etaSec > 0 && (
                    <span>Осталось: ~{Math.floor(videoProcessingInfo.etaSec / 60)}:{String(videoProcessingInfo.etaSec % 60).padStart(2, '0')}</span>
                  )}
                </div>
                {videoFileInfo && (
                  <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.75rem' }}>
                    {videoFileInfo.name} ({videoFileInfo.sizeMb} МБ)
                  </p>
                )}
                {/* Step indicators */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  {['360p', '480p', '720p', '1080p'].map((q, i) => {
                    const done = videoProcessingInfo.progress > ((i + 1) / 3) * 100;
                    const active = videoProcessingInfo.step && videoProcessingInfo.step.includes(q);
                    return (
                      <div key={q} style={{
                        padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 500,
                        background: done ? 'rgba(34,197,94,0.2)' : active ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
                        color: done ? '#4ade80' : active ? '#a5b4fc' : '#64748b',
                        border: `1px solid ${done ? 'rgba(34,197,94,0.3)' : active ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.1)'}`,
                        transition: 'all 0.3s ease'
                      }}>
                        {done ? '✓ ' : active ? '◉ ' : '○ '}{q}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              /* === Upload Phase === */
              <>
                <div style={{ 
                  width: '56px', height: '56px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '1rem'
                }}>
                  <Loader2 className="animate-spin" size={28} style={{ color: '#fff' }} />
                </div>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem', color: '#e0e7ff' }}>
                  {t('gamePageUI.uploadingVideosServer')}
                </p>
                {videoFileInfo && (
                  <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '1rem' }}>
                    {videoFileInfo.name} ({videoFileInfo.sizeMb} МБ)
                  </p>
                )}
                {/* Upload progress bar */}
                <div style={{
                  width: '85%', maxWidth: '450px', height: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '5px', overflow: 'hidden', marginBottom: '0.75rem'
                }}>
                  <div style={{
                    width: `${videoUploadProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                    borderRadius: '5px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', color: '#94a3b8' }}>
                  <span style={{ fontWeight: 600, color: '#e0e7ff' }}>{videoUploadProgress}%</span>
                  {videoUploadSpeed > 0 && (
                    <span>{videoUploadSpeed.toFixed(1)} МБ/с</span>
                  )}
                  {videoUploadEta > 0 && videoUploadProgress < 100 && (
                    <span>Осталось: ~{videoUploadEta < 60 ? `${videoUploadEta} сек` : `${Math.floor(videoUploadEta / 60)} мин ${videoUploadEta % 60} сек`}</span>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (game.video_path || game.video_hls_ready) && hasRole(['admin', 'editor']) ? (
          <>
          {/* Video Player (Visible when video exists) */}
          <div
            className="game-fullbleed-media"
            style={{
              position: 'relative',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
            <InteractiveGamePlayer
              gameId={gameId}
              poster={game.video_cover_path ? `${API}/games/${gameId}/video-cover?t=${Date.now()}` : undefined}
              style={{
                width: '100%',
                maxHeight: '600px',
                display: 'block'
              }}
              getAuthHeader={getAuthHeader}
              coverOnly={true}
            />
            
            {/* Control Icons Overlay (Admin/Editor only) */}
            {hasRole(['admin', 'editor']) && (
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                display: 'flex',
                gap: '8px',
                zIndex: 10
              }}>
                {/* Delete Video Button */}
                <button
                  onClick={handleVideoDelete}
                  disabled={uploadingVideo}
                  title={t('gamePageUI.deleteVideo')}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.9)',
                    color: 'white',
                    border: 'none',
                    cursor: uploadingVideo ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: uploadingVideo ? 0.5 : 1,
                    transition: 'all 0.2s',
                    backdropFilter: 'blur(4px)'
                  }}
                  onMouseEnter={(e) => { e.target.style.background = 'rgba(239, 68, 68, 1)' }}
                  onMouseLeave={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.9)' }}
                >
                  {uploadingVideo ? <Loader2 size={20} className="animate-spin" /> : <Trash2 size={20} />}
                </button>
                
                {/* Cover Upload/Delete Button */}
                {game.video_cover_path ? (
                  <button
                    onClick={handleVideoCoverDelete}
                    disabled={uploadingVideoCover}
                    title={t('gamePageUI.deleteCover')}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: 'rgba(220, 53, 69, 0.9)',
                      color: 'white',
                      border: 'none',
                      cursor: uploadingVideoCover ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: uploadingVideoCover ? 0.5 : 1,
                      transition: 'all 0.2s',
                      backdropFilter: 'blur(4px)'
                    }}
                    onMouseEnter={(e) => { e.target.style.background = 'rgba(220, 53, 69, 1)' }}
                    onMouseLeave={(e) => { e.target.style.background = 'rgba(220, 53, 69, 0.9)' }}
                  >
                    {uploadingVideoCover ? <Loader2 size={20} className="animate-spin" /> : <X size={20} />}
                  </button>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleVideoCoverSelect}
                      style={{ display: 'none' }}
                      id="main-video-cover-input"
                    />
                    <label
                      htmlFor="main-video-cover-input"
                      title={t('gamePageUI.uploadCover')}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: 'rgba(16, 185, 129, 0.9)',
                        color: 'white',
                        border: 'none',
                        cursor: uploadingVideoCover ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: uploadingVideoCover ? 0.5 : 1,
                        transition: 'all 0.2s',
                        backdropFilter: 'blur(4px)'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(16, 185, 129, 1)' }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.9)' }}
                    >
                      {uploadingVideoCover ? <Loader2 size={20} className="animate-spin" /> : <ImageIcon size={20} />}
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        </>
        ) : null}

        {/* External Video Player (VKVideo, YouTube, etc.) */}
        {game.external_video_embed_url && (
          <div style={{
            position: 'relative',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            background: '#000'
          }}>
            {/* Embed iframe for VK/YouTube/Rutube */}
            <iframe
              src={game.external_video_embed_url}
              style={{
                width: '100%',
                height: '450px',
                border: 'none',
                display: 'block'
              }}
              allowFullScreen
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            />
            
            {/* Control overlay for external video */}
            {hasRole(['admin', 'editor']) && (
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                display: 'flex',
                gap: '8px',
                zIndex: 10
              }}>
                {/* Delete button */}
                <button
                  onClick={async () => {
                    if (!window.confirm('Удалить внешнее видео?')) return;
                    try {
                      await axios.delete(`${API}/games/${gameId}/external-video`, { headers: getAuthHeader() });
                      await fetchGameData();
                    } catch (err) {
                      alert('Ошибка удаления');
                    }
                  }}
                  title="Удалить видео"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.9)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            )}
            
            {/* Source info */}
            <div style={{
              padding: '0.5rem 1rem',
              background: 'rgba(0,0,0,0.8)',
              color: '#9ca3af',
              fontSize: '0.8rem'
            }}>
              Источник: {game.external_video_url}
            </div>
          </div>
        )}

        {/* Fallback: Show external video URL without embed (when embed failed) */}
        {game.external_video_url && !game.external_video_embed_url && hasRole(['admin', 'editor']) && (
          <div style={{
            padding: '1rem',
            background: '#fef3c7',
            borderRadius: '12px',
            border: '1px solid #f59e0b',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '0.25rem' }}>
                  {t('gamePageUI.externalVideoCouldnT')}
                </div>
                <a 
                  href={game.external_video_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#b45309', fontSize: '0.9rem', wordBreak: 'break-all' }}
                >
                  {game.external_video_url}
                </a>
              </div>
              <button
                onClick={async () => {
                  if (!window.confirm('Удалить ссылку на внешнее видео?')) return;
                  try {
                    await axios.delete(`${API}/games/${gameId}/external-video`, { headers: getAuthHeader() });
                    await fetchGameData();
                  } catch (err) {
                    alert('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
                  }
                }}
                title={t('gamePageUI.deleteLink')}
                style={{
                  padding: '8px 16px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 500
                }}
              >
                <Trash2 size={18} />
                {t('gamePageUI.remove')}
              </button>
            </div>
          </div>
        )}

        {/* Interactive Video Button and Timecode Editor */}
        {(game.video_path || game.video_hls_ready || game.external_video_url) && (
          <div style={{ marginTop: '1rem' }}>
            {/* Start Interactive Video Button */}
            

            {/* Timecode Editor (Admin/Editor only) */}
            {hasRole(['admin', 'editor']) && (
              <VideoTimecodeEditor
                gameId={gameId}
                panoramas={panoramas}
                getAuthHeader={getAuthHeader}
                externalVideoUrl={game.external_video_direct_url}
              />
            )}
          </div>
        )}
      </div>
              </>
            )
          },
          // Section 2: Game Status & Winners Podium (hidden for objects_only games — no winners)
          ...(game?.game_mode !== 'objects_only' ? [{
            title: (
              <span className="game-status-title">
                {winners.length < 3 && (
                  <span className="main-title">{t('gamePageUI.gameActive')}</span>
                )}
                <span className="sub-title">
                  {winners.length < 3 
                    ? t('game.canWinPrize')
                    : t('game.allPrizesTaken')}
                </span>
              </span>
            ),
            content: (
              <>
      {/* Winners Podium */}
      {panoramas.length > 0 && (
        <WinnersPodium 
          winners={winners} 
          totalPanoramas={panoramas.length}
          gameId={gameId}
          game={game}
          currentUserId={user?.id}
        />
      )}
              </>
            )
          }] : []),
          // Section 3: Prizes - only show if admin/editor OR if any prizes exist (and not objects_only)
          ...(game?.game_mode !== 'objects_only' && (hasRole(['admin', 'editor']) || 
              game.first_prize_image || game.first_prize_video || 
              game.second_prize_image || game.second_prize_video || 
              game.third_prize_image || game.third_prize_video)) ? [{
            title: t('game.prizes'),
            content: (
              <>
      {/* Prizes Section (Always visible for admins/editors, or if prizes exist) */}
      {(hasRole(['admin', 'editor']) || 
        game.first_prize_image || game.first_prize_video || 
        game.second_prize_image || game.second_prize_video || 
        game.third_prize_image || game.third_prize_video) && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            gap: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {/* First Place Prize */}
            {(hasRole(['admin', 'editor']) || game.first_prize_image || game.first_prize_video) && (
              <div style={{
                flex: '1',
                minWidth: '250px',
                maxWidth: '350px',
                background: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                border: '3px solid #FFD700'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '1rem',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#2d3748'
                }}>
                  <PlaceMedal place={1} size="sm" /> {t('gamePageUI.1stPlace')}
                </div>
                
                {/* Image */}
                {game.first_prize_image ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <img
                      src={`${API}/games/${gameId}/prize/1/image?t=${Date.now()}`}
                      alt="1st place prize"
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        marginBottom: '0.5rem'
                      }}
                    />
                    {hasRole(['admin', 'editor']) && (
                      <button
                        onClick={() => handlePrizeDelete(1, 'image')}
                        disabled={uploadingPrize === '1-image'}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: uploadingPrize === '1-image' ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        {uploadingPrize === '1-image' ? 'Удаление...' : 'Удалить фото'}
                      </button>
                    )}
                  </div>
                ) : hasRole(['admin', 'editor']) && (
                  <div style={{ marginBottom: '1rem' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handlePrizeUpload(1, 'image', file);
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                      id="first-prize-image"
                    />
                    <label
                      htmlFor="first-prize-image"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f3f4f6',
                        border: '2px dashed #9ca3af',
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: uploadingPrize === '1-image' ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        color: '#6b7280'
                      }}
                    >
                      {uploadingPrize === '1-image' ? 'Загрузка...' : '📷 Загрузить фото'}
                    </label>
                  </div>
                )}
                
                {/* Video */}
                {uploadingPrize === '1-video' ? (
                  <div style={{
                    width: '100%',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    color: '#fff',
                    padding: '1.5rem'
                  }}>
                    <Loader2 className="animate-spin" size={32} style={{ marginBottom: '0.75rem' }} />
                    <p style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.75rem' }}>
                      {prizeVideoProgress[1] < 100 ? 'Загрузка...' : 'Обработка...'}
                    </p>
                    <div style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{
                        width: `${prizeVideoProgress[1]}%`,
                        height: '100%',
                        backgroundColor: '#FFD700',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <p style={{ fontSize: '0.875rem' }}>{prizeVideoProgress[1]}%</p>
                  </div>
                ) : game.first_prize_video ? (
                  <div style={{ position: 'relative' }}>
                    <PrizeVideoPlayer
                      gameId={gameId}
                      place={1}
                      poster={game.first_prize_video_cover ? `${API}/games/${gameId}/prize/1/video-cover?t=${Date.now()}` : undefined}
                      style={{
                        width: '100%',
                        borderRadius: '8px'
                      }}
                    />
                    {hasRole(['admin', 'editor']) && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        display: 'flex',
                        gap: '6px',
                        zIndex: 10
                      }}>
                        <button
                          onClick={() => handlePrizeDelete(1, 'video')}
                          disabled={uploadingPrize === '1-video'}
                          title="Удалить видео"
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '6px',
                            background: 'rgba(239, 68, 68, 0.9)',
                            color: 'white',
                            border: 'none',
                            cursor: uploadingPrize === '1-video' ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: uploadingPrize === '1-video' ? 0.5 : 1,
                            backdropFilter: 'blur(4px)'
                          }}
                        >
                          {uploadingPrize === '1-video' ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                        </button>
                        
                        {game.first_prize_video_cover ? (
                          <button
                            onClick={() => handlePrizeVideoCoverDelete(1)}
                            disabled={uploadingPrize === '1-video-cover'}
                            title="Удалить обложку"
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '6px',
                              background: 'rgba(220, 53, 69, 0.9)',
                              color: 'white',
                              border: 'none',
                              cursor: uploadingPrize === '1-video-cover' ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: uploadingPrize === '1-video-cover' ? 0.5 : 1,
                              backdropFilter: 'blur(4px)'
                            }}
                          >
                            {uploadingPrize === '1-video-cover' ? <Loader2 size={18} className="animate-spin" /> : <X size={18} />}
                          </button>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={(e) => handlePrizeVideoCoverUpload(1, e)}
                              style={{ display: 'none' }}
                              id="first-prize-video-cover"
                            />
                            <label
                              htmlFor="first-prize-video-cover"
                              title="Загрузить обложку"
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '6px',
                                background: 'rgba(16, 185, 129, 0.9)',
                                color: 'white',
                                border: 'none',
                                cursor: uploadingPrize === '1-video-cover' ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: uploadingPrize === '1-video-cover' ? 0.5 : 1,
                                backdropFilter: 'blur(4px)'
                              }}
                            >
                              {uploadingPrize === '1-video-cover' ? <Loader2 size={18} className="animate-spin" /> : <ImageIcon size={18} />}
                            </label>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : hasRole(['admin', 'editor']) && (
                  <div>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handlePrizeUpload(1, 'video', file);
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                      id="first-prize-video"
                    />
                    <label
                      htmlFor="first-prize-video"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f3f4f6',
                        border: '2px dashed #9ca3af',
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: '#6b7280'
                      }}
                    >
                      {t('gamePageUI.uploadVideo2')}
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Second Place Prize */}
            {(hasRole(['admin', 'editor']) || game.second_prize_image || game.second_prize_video) && (
              <div style={{
                flex: '1',
                minWidth: '250px',
                maxWidth: '350px',
                background: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                border: '3px solid #C0C0C0'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '1rem',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#2d3748'
                }}>
                  <PlaceMedal place={2} size="sm" /> {t('gamePageUI.2ndPlace')}
                </div>
                
                {/* Image */}
                {game.second_prize_image ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <img
                      src={`${API}/games/${gameId}/prize/2/image?t=${Date.now()}`}
                      alt="2nd place prize"
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        marginBottom: '0.5rem'
                      }}
                    />
                    {hasRole(['admin', 'editor']) && (
                      <button
                        onClick={() => handlePrizeDelete(2, 'image')}
                        disabled={uploadingPrize === '2-image'}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: uploadingPrize === '2-image' ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        {uploadingPrize === '2-image' ? 'Удаление...' : 'Удалить фото'}
                      </button>
                    )}
                  </div>
                ) : hasRole(['admin', 'editor']) && (
                  <div style={{ marginBottom: '1rem' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handlePrizeUpload(2, 'image', file);
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                      id="second-prize-image"
                    />
                    <label
                      htmlFor="second-prize-image"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f3f4f6',
                        border: '2px dashed #9ca3af',
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: uploadingPrize === '2-image' ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        color: '#6b7280'
                      }}
                    >
                      {uploadingPrize === '2-image' ? 'Загрузка...' : '📷 Загрузить фото'}
                    </label>
                  </div>
                )}
                
                {/* Video */}
                {uploadingPrize === '2-video' ? (
                  <div style={{
                    width: '100%',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    color: '#fff',
                    padding: '1.5rem'
                  }}>
                    <Loader2 className="animate-spin" size={32} style={{ marginBottom: '0.75rem' }} />
                    <p style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.75rem' }}>
                      {prizeVideoProgress[2] < 100 ? 'Загрузка...' : 'Обработка...'}
                    </p>
                    <div style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{
                        width: `${prizeVideoProgress[2]}%`,
                        height: '100%',
                        backgroundColor: '#C0C0C0',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <p style={{ fontSize: '0.875rem' }}>{prizeVideoProgress[2]}%</p>
                  </div>
                ) : game.second_prize_video ? (
                  <div style={{ position: 'relative' }}>
                    <PrizeVideoPlayer
                      gameId={gameId}
                      place={2}
                      poster={game.second_prize_video_cover ? `${API}/games/${gameId}/prize/2/video-cover?t=${Date.now()}` : undefined}
                      style={{
                        width: '100%',
                        borderRadius: '8px'
                      }}
                    />
                    {hasRole(['admin', 'editor']) && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        display: 'flex',
                        gap: '6px',
                        zIndex: 10
                      }}>
                        <button
                          onClick={() => handlePrizeDelete(2, 'video')}
                          disabled={uploadingPrize === '2-video'}
                          title="Удалить видео"
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '6px',
                            background: 'rgba(239, 68, 68, 0.9)',
                            color: 'white',
                            border: 'none',
                            cursor: uploadingPrize === '2-video' ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: uploadingPrize === '2-video' ? 0.5 : 1,
                            backdropFilter: 'blur(4px)'
                          }}
                        >
                          {uploadingPrize === '2-video' ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                        </button>
                        
                        {game.second_prize_video_cover ? (
                          <button
                            onClick={() => handlePrizeVideoCoverDelete(2)}
                            disabled={uploadingPrize === '2-video-cover'}
                            title="Удалить обложку"
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '6px',
                              background: 'rgba(220, 53, 69, 0.9)',
                              color: 'white',
                              border: 'none',
                              cursor: uploadingPrize === '2-video-cover' ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: uploadingPrize === '2-video-cover' ? 0.5 : 1,
                              backdropFilter: 'blur(4px)'
                            }}
                          >
                            {uploadingPrize === '2-video-cover' ? <Loader2 size={18} className="animate-spin" /> : <X size={18} />}
                          </button>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={(e) => handlePrizeVideoCoverUpload(2, e)}
                              style={{ display: 'none' }}
                              id="second-prize-video-cover"
                            />
                            <label
                              htmlFor="second-prize-video-cover"
                              title="Загрузить обложку"
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '6px',
                                background: 'rgba(16, 185, 129, 0.9)',
                                color: 'white',
                                border: 'none',
                                cursor: uploadingPrize === '2-video-cover' ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: uploadingPrize === '2-video-cover' ? 0.5 : 1,
                                backdropFilter: 'blur(4px)'
                              }}
                            >
                              {uploadingPrize === '2-video-cover' ? <Loader2 size={18} className="animate-spin" /> : <ImageIcon size={18} />}
                            </label>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : hasRole(['admin', 'editor']) && (
                  <div>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handlePrizeUpload(2, 'video', file);
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                      id="second-prize-video"
                    />
                    <label
                      htmlFor="second-prize-video"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f3f4f6',
                        border: '2px dashed #9ca3af',
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: '#6b7280'
                      }}
                    >
                      🎥 Загрузить видео
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Third Place Prize */}
            {(hasRole(['admin', 'editor']) || game.third_prize_image || game.third_prize_video) && (
              <div style={{
                flex: '1',
                minWidth: '250px',
                maxWidth: '350px',
                background: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                border: '3px solid #CD7F32'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '1rem',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#2d3748'
                }}>
                  <PlaceMedal place={3} size="sm" /> {t('gamePageUI.3rdPlace')}
                </div>
                
                {/* Image */}
                {game.third_prize_image ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <img
                      src={`${API}/games/${gameId}/prize/3/image?t=${Date.now()}`}
                      alt="3rd place prize"
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        marginBottom: '0.5rem'
                      }}
                    />
                    {hasRole(['admin', 'editor']) && (
                      <button
                        onClick={() => handlePrizeDelete(3, 'image')}
                        disabled={uploadingPrize === '3-image'}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: uploadingPrize === '3-image' ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        {uploadingPrize === '3-image' ? 'Удаление...' : 'Удалить фото'}
                      </button>
                    )}
                  </div>
                ) : hasRole(['admin', 'editor']) && (
                  <div style={{ marginBottom: '1rem' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handlePrizeUpload(3, 'image', file);
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                      id="third-prize-image"
                    />
                    <label
                      htmlFor="third-prize-image"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f3f4f6',
                        border: '2px dashed #9ca3af',
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: uploadingPrize === '3-image' ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        color: '#6b7280'
                      }}
                    >
                      {uploadingPrize === '3-image' ? 'Загрузка...' : '📷 Загрузить фото'}
                    </label>
                  </div>
                )}
                
                {/* Video */}
                {uploadingPrize === '3-video' ? (
                  <div style={{
                    width: '100%',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    color: '#fff',
                    padding: '1.5rem'
                  }}>
                    <Loader2 className="animate-spin" size={32} style={{ marginBottom: '0.75rem' }} />
                    <p style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.75rem' }}>
                      {prizeVideoProgress[3] < 100 ? 'Загрузка...' : 'Обработка...'}
                    </p>
                    <div style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{
                        width: `${prizeVideoProgress[3]}%`,
                        height: '100%',
                        backgroundColor: '#CD7F32',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <p style={{ fontSize: '0.875rem' }}>{prizeVideoProgress[3]}%</p>
                  </div>
                ) : game.third_prize_video ? (
                  <div style={{ position: 'relative' }}>
                    <PrizeVideoPlayer
                      gameId={gameId}
                      place={3}
                      poster={game.third_prize_video_cover ? `${API}/games/${gameId}/prize/3/video-cover?t=${Date.now()}` : undefined}
                      style={{
                        width: '100%',
                        borderRadius: '8px'
                      }}
                    />
                    {hasRole(['admin', 'editor']) && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        display: 'flex',
                        gap: '6px',
                        zIndex: 10
                      }}>
                        <button
                          onClick={() => handlePrizeDelete(3, 'video')}
                          disabled={uploadingPrize === '3-video'}
                          title="Удалить видео"
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '6px',
                            background: 'rgba(239, 68, 68, 0.9)',
                            color: 'white',
                            border: 'none',
                            cursor: uploadingPrize === '3-video' ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: uploadingPrize === '3-video' ? 0.5 : 1,
                            backdropFilter: 'blur(4px)'
                          }}
                        >
                          {uploadingPrize === '3-video' ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                        </button>
                        
                        {game.third_prize_video_cover ? (
                          <button
                            onClick={() => handlePrizeVideoCoverDelete(3)}
                            disabled={uploadingPrize === '3-video-cover'}
                            title="Удалить обложку"
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '6px',
                              background: 'rgba(220, 53, 69, 0.9)',
                              color: 'white',
                              border: 'none',
                              cursor: uploadingPrize === '3-video-cover' ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: uploadingPrize === '3-video-cover' ? 0.5 : 1,
                              backdropFilter: 'blur(4px)'
                            }}
                          >
                            {uploadingPrize === '3-video-cover' ? <Loader2 size={18} className="animate-spin" /> : <X size={18} />}
                          </button>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={(e) => handlePrizeVideoCoverUpload(3, e)}
                              style={{ display: 'none' }}
                              id="third-prize-video-cover"
                            />
                            <label
                              htmlFor="third-prize-video-cover"
                              title="Загрузить обложку"
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '6px',
                                background: 'rgba(16, 185, 129, 0.9)',
                                color: 'white',
                                border: 'none',
                                cursor: uploadingPrize === '3-video-cover' ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: uploadingPrize === '3-video-cover' ? 0.5 : 1,
                                backdropFilter: 'blur(4px)'
                              }}
                            >
                              {uploadingPrize === '3-video-cover' ? <Loader2 size={18} className="animate-spin" /> : <ImageIcon size={18} />}
                            </label>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : hasRole(['admin', 'editor']) && (
                  <div>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handlePrizeUpload(3, 'video', file);
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                      id="third-prize-video"
                    />
                    <label
                      htmlFor="third-prize-video"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f3f4f6',
                        border: '2px dashed #9ca3af',
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: '#6b7280'
                      }}
                    >
                      🎥 Загрузить видео
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
              </>
            )
          }] : [],
          // Section 4: Photos (Panoramas)
          {
            title: t('game.tasks'),
            content: (
              <>
      {/* My Score */}
      {myResults && myResults.answered_count > 0 && (
        <div className="results-card">
          <div className="results-container">
            <div className="results-info">
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                {t('gamePageUI.result')}
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.95rem', opacity: 0.9 }}>
                {myResults.answered_count} из {myResults.total_panoramas} ответов
              </p>
              <p style={{ 
                margin: '0.5rem 0 0 0', 
                fontSize: '0.9rem', 
                opacity: 0.95,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                <span style={{ fontSize: '1.1rem' }}>
                  {(() => {
                    const sortedResults = [...results].sort((a, b) => b.total_score - a.total_score);
                    const userPosition = sortedResults.findIndex(r => r.user_id === user?.id) + 1;
                    if (userPosition >= 1 && userPosition <= 3) {
                      return <PlaceMedal place={userPosition} size="sm" />;
                    }
                    return userPosition;
                  })()}
                </span>
                <span>
                  {(() => {
                    const sortedResults = [...results].sort((a, b) => b.total_score - a.total_score);
                    const userPosition = sortedResults.findIndex(r => r.user_id === user?.id) + 1;
                    const totalParticipants = results.length;
                    const positionText = userPosition <= 3 ? '' : `${userPosition} место`;
                    const participantText = totalParticipants === 1 ? 'участника' : totalParticipants < 5 ? 'участников' : 'участников';
                    return `${positionText} из ${totalParticipants} ${participantText}`;
                  })()}
                </span>
              </p>
            </div>
            
            <div className="results-accuracy">
              <div className="accuracy-number">
                {myResults.total_panoramas > 0 
                  ? ((myResults.total_score / (myResults.total_panoramas * 5000)) * 100).toFixed(1)
                  : 0}%
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '0.5rem' }}>
                {t('gamePageUI.accuracy')}
              </div>
            </div>

            <div className="results-score">
              <div className="score-number">
                <Trophy size={28} style={{ display: 'inline', marginRight: '0.5rem', marginBottom: '0.25rem' }} />
                {myResults.total_score}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '0.25rem' }}>
                из {myResults.total_panoramas * 5000} очков
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panoramas Grid */}
      {panoramas.length === 0 ? (
        <div className="empty-state">
          <Upload size={64} strokeWidth={1} />
          <h2>{t('gamePageUI.thereNoPanoramasGame')}</h2>
          {hasRole(['admin', 'editor']) && (
            <p>{t('gamePageUI.clickAddPanoramaDownload')}</p>
          )}
        </div>
      ) : (
        <div className="gallery-grid" data-testid="panorama-grid">
          {panoramas.map((panorama, index) => {
            const result = getPanoramaResult(panorama.id);
            
            return (
              <div
                key={panorama.id}
                className="panorama-card"
                onClick={() => {
                  if (panorama.latitude && panorama.longitude) {
                    navigate(`/games/${gameId}/guess/${panorama.id}`);
                  } else {
                    navigate(`/view/${panorama.id}`);
                  }
                }}
                data-testid={`panorama-card-${panorama.id}`}
                draggable={hasRole(['admin', 'editor'])}
                onDragStart={(e) => handlePanoramaDragStart(e, panorama, index)}
                onDragOver={(e) => handlePanoramaDragOver(e, index)}
                onDragLeave={handlePanoramaDragLeave}
                onDrop={(e) => handlePanoramaDrop(e, index)}
                onDragEnd={handlePanoramaDragEnd}
                style={{ 
                  position: 'relative',
                  cursor: hasRole(['admin', 'editor']) ? 'move' : 'pointer',
                  opacity: draggedPanorama?.index === index ? 0.5 : 1,
                  border: dragOverIndex === index ? '2px dashed #667eea' : 'none',
                  transition: 'opacity 0.2s, border 0.2s'
                }}
              >
                <div className="panorama-preview">
                  {(panorama.preview_path || panorama.custom_preview_path) ? (
                    <img
                      src={`${API}/panoramas/${panorama.id}/preview?v=${encodeURIComponent(panorama.custom_preview_updated_at || panorama.upload_date || panorama.id)}`}
                      alt={panorama.name}
                      data-testid={`panorama-preview-${panorama.id}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                  ) : (
                    <div className="no-preview">
                      <Upload size={32} strokeWidth={1} />
                    </div>
                  )}
                  
                  {/* Warning icon if no coordinates - only for admin/editor */}
                  {(!panorama.latitude || !panorama.longitude) && hasRole(['admin', 'editor']) && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      background: 'rgba(239, 68, 68, 0.9)',
                      color: 'white',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} title={t('gamePageUI.panoramaNoGpsCoordinates')}>
                      <MapPin size={14} />
                      <span>{t('gamePageUI.thereNoCoordinates')}</span>
                    </div>
                  )}
                  
                  {/* Result Overlay */}
                  {result && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.75)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      padding: '1rem'
                    }}>
                      <Trophy size={32} style={{ marginBottom: '0.5rem' }} />
                      <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                        {result.score}
                      </div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '0.25rem' }}>
                        {result.distance < 1000 
                          ? `${Math.round(result.distance)} м`
                          : `${(result.distance / 1000).toFixed(2)} км`
                        }
                      </div>
                    </div>
                  )}

                  {/* Hotspot progress badge — only when at least one object found on this panorama */}
                  {game?.game_mode === 'objects_only' && (() => {
                    const panoHotspots = allHotspots.filter(h => h.panorama_id === panorama.id);
                    const total = panoHotspots.length;
                    const found = panoHotspots.filter(h => foundHotspotIds.includes(h.id)).length;
                    if (total === 0 || found === 0) return null;
                    return (
                      <div
                        data-testid={`panorama-hotspots-progress-${panorama.id}`}
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          left: '8px',
                          background: 'rgba(245, 158, 11, 0.95)',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                          zIndex: 5
                        }}
                        title={t('gamePageUI.objectsFound') || 'Найдено объектов'}
                      >
                        <Search size={12} />
                        <span>{found}/{total}</span>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Show edit button only for admin/editor */}
                {hasRole(['admin', 'editor']) && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 10,
                    display: 'flex',
                    gap: '0.5rem'
                  }}>
                    <button
                      className="edit-coords-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/edit/${panorama.id}`);
                      }}
                      title={t('gamePageUI.editCoordinates')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      <MapPin size={16} />
                    </button>
                    
                    {/* Hotspot editor button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/hotspots/${panorama.id}`);
                      }}
                      title={t('gamePageUI.editInteractiveZones')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#8b5cf6'
                      }}
                    >
                      <Target size={16} />
                    </button>
                    
                    {/* Hint polygon editor button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openHintPolygonEditor(panorama);
                      }}
                      title={hintPolygonExisting[panorama.id] ? "Редактировать полигон подсказки" : "Создать полигон подсказки"}
                      data-testid={`hint-polygon-btn-${panorama.id}`}
                      style={{
                        background: hintPolygonExisting[panorama.id] ? 'rgba(245, 158, 11, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        border: 'none',
                        cursor: 'pointer',
                        color: hintPolygonExisting[panorama.id] ? '#fff' : '#f59e0b'
                      }}
                    >
                      <Waypoints size={16} />
                    </button>
                    
                    {/* Custom preview upload button */}
                    <input
                      type="file"
                      ref={(el) => previewFileInputRefs.current[panorama.id] = el}
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.files && e.target.files[0]) {
                          handleUploadCustomPreview(panorama.id, e.target.files[0]);
                          e.target.value = '';
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        previewFileInputRefs.current[panorama.id]?.click();
                      }}
                      disabled={uploadingPreview === panorama.id}
                      title={t('gamePageUI.uploadCustomPreview')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        border: 'none',
                        cursor: uploadingPreview === panorama.id ? 'not-allowed' : 'pointer',
                        color: '#3b82f6'
                      }}
                    >
                      {uploadingPreview === panorama.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <ImageIcon size={16} />
                      )}
                    </button>
                    
                    {/* Delete custom preview button - only show if custom preview exists */}
                    {panorama.custom_preview_path && (
                      <button
                        onClick={(e) => handleDeleteCustomPreview(panorama.id, e)}
                        disabled={uploadingPreview === panorama.id}
                        title={t('gamePageUI.deleteCustomPreview')}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          border: 'none',
                          cursor: uploadingPreview === panorama.id ? 'not-allowed' : 'pointer',
                          color: '#f59e0b'
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                    
                    <button
                      onClick={(e) => handleDeletePanorama(panorama.id, e)}
                      disabled={deleting === panorama.id}
                      title={t('gamePageUI.deletePanorama')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        border: 'none',
                        cursor: deleting === panorama.id ? 'not-allowed' : 'pointer',
                        color: '#dc2626'
                      }}
                    >
                      {deleting === panorama.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
              </>
            )
          },
          // Section 5: Results Table
          {
            title: t('game.leaderboard'),
            content: (results.length > 0 || true) && (
              <div style={{ marginTop: '2rem' }}>
                <ResultsTable
                  gameId={game.id}
                  results={results}
                  winners={winners}
                  game={game}
                  currentUserId={user?.id}
                  currentUser={user}
                  showPrizePlaceholders={true}
                  itemsPerPage={15}
                  onDeleteResult={handleDeleteResult}
                />
              </div>
            )
          }
        ];
        return sections;
        })()}
        defaultOpen={(() => {
          // Calculate the index of sections dynamically
          const isObjectsOnly = game?.game_mode === 'objects_only';
          const sections = [
            { title: "Описание" },                    // 0
            ...(isObjectsOnly ? [] : [{ title: "Игра активна" }]),
            ...(!isObjectsOnly && (hasRole(['admin', 'editor']) ||
                game.first_prize_image || game.first_prize_video || 
                game.second_prize_image || game.second_prize_video || 
                game.third_prize_image || game.third_prize_video) ? [{ title: "Призы" }] : []),
            { title: "Задания" }
          ];
          
          const openSections = [];
          
          // Always open "Задания"
          const zadaniyaIndex = sections.findIndex(s => s.title === "Задания");
          if (zadaniyaIndex !== -1) openSections.push(zadaniyaIndex);
          
          // Open "Игра активна" (Статус игры) if there's at least one winner
          if (winners && winners.length > 0) {
            const statusIndex = sections.findIndex(s => s.title === "Игра активна");
            if (statusIndex !== -1) openSections.push(statusIndex);
          }
          
          // Open "Описание" when video exists
          if (game.video_path || game.video_hls_ready || game.external_video_url) {
            const opisanieIndex = sections.findIndex(s => s.title === "Описание");
            if (opisanieIndex !== -1) openSections.push(opisanieIndex);
          }
          
          return openSections;
        })()}
      />

      {/* All Answers Map Modal */}
      <AllAnswersMap
        isOpen={showAllAnswersMap}
        onClose={() => setShowAllAnswersMap(false)}
        gameId={gameId}
        myResults={myResults}
        panoramas={panoramas}
        winnersComplete={winners.length === 3}
      />

      {/* Progress Modal for JPG Uploads */}
      {showProgressModal && uploadingJpgs.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          zIndex: 1000,
          minWidth: '400px',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                {uploadingJpgs.filter(i => i.status === 'completed').length === uploadingJpgs.length 
                  ? '✓ Обработка завершена' 
                  : `Обработка панорам (${uploadingJpgs.filter(i => i.status === 'completed').length}/${uploadingJpgs.length})`}
              </h3>
              {uploadingJpgs.length > 0 && (
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                  {uploadingJpgs.filter(i => i.status === 'uploading').length > 0 && `Загрузка: ${uploadingJpgs.filter(i => i.status === 'uploading').length} `}
                  {uploadingJpgs.filter(i => i.status === 'processing').length > 0 && `Обработка: ${uploadingJpgs.filter(i => i.status === 'processing').length} `}
                  {uploadingJpgs.filter(i => i.status === 'completed').length > 0 && `Готово: ${uploadingJpgs.filter(i => i.status === 'completed').length}`}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowProgressModal(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={20} />
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {uploadingJpgs.map((item, index) => (
              <div
                key={item.id}
                style={{
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>Панорама #{index + 1}</span>
                  {item.status === 'uploading' && (
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{item.progress}%</span>
                  )}
                  {item.status === 'processing' && (
                    <span style={{ fontSize: '12px', color: '#6366f1' }}>{item.progress || 0}%</span>
                  )}
                  {item.status === 'completed' && (
                    <span style={{ color: '#10b981', fontSize: '14px' }}>✓</span>
                  )}
                  {item.status === 'error' && (
                    <span style={{ color: '#ef4444', fontSize: '14px' }}>✗</span>
                  )}
                </div>

                {/* Show detailed status message */}
                {item.message && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    {item.message}
                  </div>
                )}

                {(item.status === 'uploading' || item.status === 'processing') && (
                  <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${item.progress}%`,
                      height: '100%',
                      backgroundColor: '#6366f1',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                )}

                {item.status === 'processing' && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      {item.message || 'Обработка...'}
                    </div>
                    <div style={{
                      width: '100%',
                      height: '4px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${item.progress || 0}%`,
                        height: '100%',
                        backgroundColor: '#6366f1',
                        transition: 'width 0.3s'
                      }} />
                    </div>
                  </div>
                )}

                {item.status === 'error' && (
                  <div style={{ fontSize: '12px', color: '#ef4444' }}>
                    {item.error || 'Произошла ошибка'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {uploadingJpgs.every(item => item.status === 'completed' || item.status === 'error') && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Button onClick={() => {
                setShowProgressModal(false);
                setUploadingJpgs([]);
              }}>
                {t('gamePageUI.close')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Backdrop for modal */}
      {showProgressModal && uploadingJpgs.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
          onClick={() => {
            if (uploadingJpgs.every(item => item.status === 'completed' || item.status === 'error')) {
              setShowProgressModal(false);
              setUploadingJpgs([]);
            }
          }}
        />
      )}

      {/* Hint Polygon Editor */}
      {hintPolygonEditor && (
        <div className="fixed inset-0 z-[2000] bg-gray-900">
          <HintPolygonMap
            panoramaId={hintPolygonEditor}
            points={hintPolygonPoints}
            setPoints={setHintPolygonPoints}
            panoramas={panoramas}
            closed={hintPolygonClosed}
            onClose={closeHintPolygon}
            mapType={hintMapType}
          />

          {/* Yellow round buttons top-left: Back + Menu */}
          <div className="absolute top-4 left-4 z-[2010] flex items-center gap-2">
            <button
              onClick={() => setHintPolygonEditor(null)}
              className="w-12 h-12 rounded-full bg-yellow-400 hover:bg-yellow-500 border-2 border-yellow-500 flex items-center justify-center shadow-lg transition-colors"
            >
              <ArrowLeft size={22} className="text-white" />
            </button>
            <div className="relative">
              <button
                onClick={() => setHintMenuOpen(!hintMenuOpen)}
                className="w-12 h-12 rounded-full bg-yellow-400 hover:bg-yellow-500 border-2 border-yellow-500 flex items-center justify-center shadow-lg transition-colors"
              >
                <Menu size={22} className="text-white" />
              </button>
              {hintMenuOpen && (
                <div className="absolute top-14 left-0 bg-white rounded-xl shadow-2xl py-2 min-w-[220px] border border-gray-200">
                  {/* Point count */}
                  {hintPolygonPoints.length > 0 && (
                    <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100 font-medium">
                      Точек: {hintPolygonPoints.length}
                    </div>
                  )}
                  {/* Undo last point */}
                  {hintPolygonPoints.length > 0 && !hintPolygonClosed && (
                    <button onClick={() => { removeLastHintPoint(); setHintMenuOpen(false); }} className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3">
                      <ArrowLeft size={16} className="text-gray-500" /> {t('gamePageUI.cancelPoint')}
                    </button>
                  )}
                  {/* Close polygon */}
                  {!hintPolygonClosed && hintPolygonPoints.length >= 3 && (
                    <button onClick={() => { closeHintPolygon(); setHintMenuOpen(false); }} className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3 text-green-600 font-medium">
                      <Target size={16} /> {t('gamePageUI.closePolygon')}
                    </button>
                  )}
                  {/* Hint cost input */}
                  <div className="px-4 py-2 border-b border-gray-100">
                    <label className="text-xs text-gray-500 mb-1 block">{t('gamePageUI.costHintPoints')}</label>
                    <input
                      type="number"
                      min="0"
                      value={hintPolygonCost}
                      onChange={(e) => setHintPolygonCost(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-amber-400"
                      placeholder={t('gamePageUI.0QuizOnly')}
                    />
                    <span className="text-xs text-gray-400 mt-1 block">{t('gamePageUI.0FreeQuizOnly')}</span>
                  </div>
                  {/* Save polygon - only for newly drawn, not loaded existing */}
                  {hintPolygonClosed && hintPolygonIsNew && (
                    <button onClick={() => { saveHintPolygon(); setHintMenuOpen(false); }} disabled={hintPolygonSaving} className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3 text-green-600 font-medium">
                      {hintPolygonSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                      {hintPolygonSaving ? 'Сохранение...' : 'Сохранить полигон'}
                    </button>
                  )}
                  {/* Save cost only (polygon already exists) */}
                  {hintPolygonExisting[hintPolygonEditor] && !hintPolygonIsNew && (
                    <button onClick={() => { saveHintPolygon(); setHintMenuOpen(false); }} disabled={hintPolygonSaving} className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3 text-green-600 font-medium">
                      {hintPolygonSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                      {hintPolygonSaving ? 'Сохранение...' : 'Сохранить настройки'}
                    </button>
                  )}
                  {/* Clear */}
                  {hintPolygonPoints.length > 0 && (
                    <button onClick={() => { cancelHintDrawing(); setHintMenuOpen(false); }} className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3">
                      <X size={16} className="text-gray-500" /> {t('gamePageUI.clear')}
                    </button>
                  )}
                  {/* Delete existing */}
                  {hintPolygonExisting[hintPolygonEditor] && (
                    <button onClick={() => { deleteHintPolygon(); setHintMenuOpen(false); }} className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3 text-red-500">
                      <Trash2 size={16} /> {t('gamePageUI.deletePolygon')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Map type switcher below back button */}
          <div className="absolute top-20 left-4 z-[2010] flex flex-col bg-white rounded shadow-lg border-2 border-black/20 overflow-hidden">
            {[
              { key: 'map', title: 'Схема', svg: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z"/><path d="M8 2v16"/><path d="M16 6v16"/></svg>' },
              { key: 'satellite', title: 'Спутник', svg: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>' },
              { key: 'hybrid', title: 'Гибрид', svg: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>' }
            ].map((t, i) => (
              <button key={t.key} onClick={() => setHintMapType(t.key)} title={t.title}
                className={'px-2.5 py-2 flex items-center justify-center ' + (i > 0 ? 'border-t border-gray-300 ' : '') + (hintMapType === t.key ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
                dangerouslySetInnerHTML={{ __html: t.svg }}
              />
            ))}
          </div>

          {/* Status indicator top-center */}
          {hintPolygonPoints.length === 0 && !hintPolygonClosed && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[2010] bg-blue-600 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium pointer-events-none">
              {t('gamePageUI.clickMapStartCreating')}
            </div>
          )}
          {hintPolygonPoints.length > 0 && !hintPolygonClosed && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[2010] bg-purple-600 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium pointer-events-none">
              {hintPolygonPoints.length} точек {hintPolygonPoints.length >= 3 ? '• Кликните на первую точку для замыкания' : ''}
            </div>
          )}

          {/* Click overlay to close menu */}
          {hintMenuOpen && (
            <div className="absolute inset-0 z-[2009]" onClick={() => setHintMenuOpen(false)} />
          )}
        </div>
      )}

    </div>
    <SiteFooter />
    </>
  );
};

// Hint polygon map component

const HintPolygonMap = ({ panoramaId, points, setPoints, panoramas, closed, onClose, mapType }) => {
  const mapRef = useRef(null);
  const pano = panoramas.find(p => p.id === panoramaId);
  const center = pano ? [pano.latitude || 55.75, pano.longitude || 37.62] : [55.75, 37.62];

  const tileUrls = {
    map: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  };

  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        if (closed) return;
        if (points.length >= 3) {
          const first = points[0];
          const map = e.target;
          const firstPx = map.latLngToContainerPoint(L.latLng(first[0], first[1]));
          const clickPx = map.latLngToContainerPoint(e.latlng);
          if (firstPx.distanceTo(clickPx) < 20) {
            onClose();
            return;
          }
        }
        setPoints(prev => [...prev, [e.latlng.lat, e.latlng.lng]]);
      }
    });
    return null;
  };

  return (
    <MapContainer center={center} zoom={16} style={{ width: '100%', height: '100%' }} zoomControl={false} ref={mapRef}>
      {mapType === 'hybrid' ? (
        <>
          <TileLayer url={tileUrls.satellite} maxZoom={19} />
          <TileLayer url={tileUrls.map} maxZoom={19} opacity={0.35} />
        </>
      ) : (
        <TileLayer key={mapType} url={tileUrls[mapType] || tileUrls.map} maxZoom={19} />
      )}
      <MapClickHandler />

      {points.map((p, i) => {
        const isFirst = i === 0;
        const canClose = isFirst && points.length >= 3 && !closed;
        return (
          <Marker key={i} position={p} icon={L.divIcon({
            className: '',
            html: '<div style="width:' + (canClose ? '20' : '14') + 'px;height:' + (canClose ? '20' : '14') + 'px;background:' + (isFirst ? '#22c55e' : '#7c3aed') + ';border:2px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.4);cursor:' + (canClose ? 'pointer' : 'default') + '"></div>',
            iconSize: [canClose ? 20 : 14, canClose ? 20 : 14],
            iconAnchor: [canClose ? 10 : 7, canClose ? 10 : 7]
          })} />
        );
      })}

      {points.length >= 2 && (
        <Polyline positions={points} pathOptions={{ color: '#7c3aed', weight: 3, dashArray: closed ? undefined : '8,6' }} />
      )}

      {closed && points.length >= 3 && (
        <>
          <Polyline positions={[points[points.length - 1], points[0]]} pathOptions={{ color: '#7c3aed', weight: 3 }} />
          <LeafletPolygon positions={points} pathOptions={{ color: '#7c3aed', fillColor: '#7c3aed', fillOpacity: 0.15, weight: 0 }} />
        </>
      )}

      {pano && pano.latitude && <Marker position={[pano.latitude, pano.longitude]} />}
    </MapContainer>
  );
};

export default GamePage;


